import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaSpinner,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { useDarkMode } from "../../hooks/useDarkMode";
import axios from "../../apis/axiosConfig";
import { toast } from "react-toastify";
import { calculateScore } from "../../utils/seoScoreCalculator";
import ImageOptimizerSection from '../../components/ImageOptimizerSection';

const PageEndpointDetailsPage = () => {
  const { projectId, endpointId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [endpoint, setEndpoint] = useState(null);
  const [implementations, setImplementations] = useState([]);
  const [loadingImpls, setLoadingImpls] = useState(false);
  const [implementingId, setImplementingId] = useState(null);
  const [pluginConnected, setPluginConnected] = useState(false);
  const [currentPageId, setCurrentPageId] = useState(null);
  const [implementationPageId, setImplementationPageId] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [suggestionAttempted, setSuggestionAttempted] = useState(false); // Track if we've tried to load/generate suggestions
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showPositive, setShowPositive] = useState(false);
  const [showNegative, setShowNegative] = useState(false);
  const [showContentQuality, setShowContentQuality] = useState(false);
  const [showExpertise, setShowExpertise] = useState(false);
  const [showPeopleFirst, setShowPeopleFirst] = useState(false);
  const [generatingSuggestions, setGeneratingSuggestions] = useState(false);
  const [expandedKeywords, setExpandedKeywords] = useState({});
  const [expandedContentItems, setExpandedContentItems] = useState({});
  const [expandedContentIssues, setExpandedContentIssues] = useState({});
  const [expandedContentVariations, setExpandedContentVariations] = useState(
    {},
  );
  const [expandedHelpfulQuestions, setExpandedHelpfulQuestions] = useState({});
  const [expandedHeadings, setExpandedHeadings] = useState({
    h1: false,
    h2: false,
    h3: false,
    h4: false,
    h5: false,
    h6: false,
  });
  const [selectedHeadingSuggestions, setSelectedHeadingSuggestions] = useState(
    {},
  );
  const [loadedHeadingSuggestions, setLoadedHeadingSuggestions] = useState({});
  const [generatingHeadingSuggestions, setGeneratingHeadingSuggestions] =
    useState({});
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [implementing, setImplementing] = useState(false);
  const [showWordPressModal, setShowWordPressModal] = useState(false);
  const [pendingSuggestion, setPendingSuggestion] = useState(null);
  const [showSuggestedVariations, setShowSuggestedVariations] = useState(false);
  const [showIssuesFound, setShowIssuesFound] = useState(false);
  const [rollingBackId, setRollingBackId] = useState(null);
  const [lastImplementation, setLastImplementation] = useState(null);
  const latestSuggestionsRequest = useRef(0);

  const checkPluginConnection = useCallback(async () => {
    try {
      const response = await axios.get(
        "/auto-implementation/plugin-connection/check/",
        {
          params: { project_id: projectId },
        },
      );
      setPluginConnected(response.data.is_connected);
    } catch (err) {
      // console.error('Error checking plugin connection:', err);
      setPluginConnected(false);
    }
  }, [projectId]);

  const loadImplementations = useCallback(
    async (pageId) => {
      if (!pageId) return;

      setLoadingImpls(true);
      try {
        const response = await axios.get("/auto-implementation/history/", {
          params: {
            project_id: projectId,
            page_id: pageId,
            endpoint: endpointId,
          },
        });
        setImplementations(response.data.results || []);
      } catch (err) {
        // Silently fail - implementations are optional
        setImplementations([]);
      } finally {
        setLoadingImpls(false);
      }
    },
    [endpointId, projectId],
  );

  const loadAiSuggestions = useCallback(
    async (pageId) => {
      if (!pageId) return;

      const endpointLower = (endpointId || "")
        .toLowerCase()
        .replace(/\s+/g, "_");
      const isHelpfulContent = endpointLower === "helpful_content";

      const requestId = ++latestSuggestionsRequest.current;

      setLoadingSuggestions(true);
      setAiSuggestions(null);
      setSuggestionAttempted(false);

      try {
        // Map frontend endpoint IDs to backend endpoint names
        // Make it case-insensitive by converting to lowercase first
        const endpointMap = {
          seo_content: "title",
          seo_content_analysis: "title",
          title: "title",
          title_tag: "title",
          meta_description: "meta",
          meta: "meta",
          headings: "h1", // Default headings to h1
          heading: "h1",
          h1: "h1",
          h2: "h2",
          h3: "h3",
          h4: "h4",
          content: "content",
          content_quality: "content",
          readability: "content",
          images: "images",
          image_analysis: "images",
          internal_links: "links",
          links: "links",
          link_analysis: "links",
          helpful_content: "helpful_content",
        };

        const backendEndpoint = endpointMap[endpointLower] || endpointLower;

        // Try to fetch AI suggestions for this specific endpoint
        // Note: pageId should be MongoDB ObjectId (e.g., "6970aa3360032sdabf7ba8be")
        // NOT a numeric website_page_id
        const response = isHelpfulContent
          ? await axios.get(`/ai-suggestions/${pageId}/`)
          : await axios.get(`/ai-suggestions/${pageId}/${backendEndpoint}/`);

        if (requestId !== latestSuggestionsRequest.current) return;

        if (response.data && response.data.status === "success") {
          const suggestionPayload =
            response.data.suggestion ||
            response.data.suggestions ||
            response.data;
          const extractedHelpfulSuggestion =
            isHelpfulContent && suggestionPayload?.suggestions
              ? suggestionPayload.suggestions.find(
                  (s) => (s?.type || "").toLowerCase() === "content",
                )
              : null;

          if (isHelpfulContent && !extractedHelpfulSuggestion) {
            setAiSuggestions(null);
            setSuggestionAttempted(true);
            return;
          }

          const resolvedSuggestion =
            extractedHelpfulSuggestion || suggestionPayload;
          setAiSuggestions(resolvedSuggestion);
          if (isHelpfulContent) {
            toast.success("AI suggestions loaded successfully!");
          }
          setSuggestionAttempted(true);
        } else {
          setAiSuggestions(null);
          setSuggestionAttempted(true);
        }
      } catch (err) {
        if (requestId !== latestSuggestionsRequest.current) return;
        if (err.response?.status === 404) {
          if (isHelpfulContent) {
            // Avoid auto-generating helpful content to prevent CORS errors
            setAiSuggestions(null);
            setSuggestionAttempted(false);
            return;
          }
        } else if (
          err.response?.status === 400 ||
          err.response?.status === 500
        ) {
        } else {
        }
        setAiSuggestions(null);
        setSuggestionAttempted(true);
      } finally {
        if (requestId === latestSuggestionsRequest.current) {
          setLoadingSuggestions(false);
        }
      }
    },
    [endpointId],
  );

  // Helper function to poll for suggestions with retry logic
  const pollForSuggestions = useCallback(
    async (
      pageId,
      maxRetries = 12,
      delayMs = 2000,
      specificHeadingLevel = null,
    ) => {
      let retries = 0;

      const poll = async () => {
        try {
          const endpointLower = (endpointId || "")
            .toLowerCase()
            .replace(/\s+/g, "_");
          const endpointMap = {
            seo_content: "title",
            seo_content_analysis: "title",
            title: "title",
            title_tag: "title",
            meta_description: "meta",
            meta: "meta",
            headings: specificHeadingLevel || "h1", // Use specific level if provided
            heading: specificHeadingLevel || "h1",
            h1: "h1",
            h2: "h2",
            h3: "h3",
            h4: "h4",
            content: "content",
            content_quality: "content",
            readability: "content",
            images: "images",
            image_analysis: "images",
            internal_links: "links",
            links: "links",
            link_analysis: "links",
            helpful_content: "helpful_content",
          };

          const backendEndpoint = endpointMap[endpointLower] || endpointLower;
          const isHelpfulContent = endpointLower === "helpful_content";
          const response = isHelpfulContent
            ? await axios.get(`/ai-suggestions/${pageId}/`)
            : await axios.get(`/ai-suggestions/${pageId}/${backendEndpoint}/`);

          if (response.data && response.data.status === "success") {
            const suggestionPayload =
              response.data.suggestion ||
              response.data.suggestions ||
              response.data;
            const extractedHelpfulSuggestion =
              isHelpfulContent && suggestionPayload?.suggestions
                ? suggestionPayload.suggestions.find(
                    (s) => (s?.type || "").toLowerCase() === "content",
                  )
                : null;

            if (isHelpfulContent && !extractedHelpfulSuggestion) {
              return false;
            }

            const resolvedSuggestion =
              extractedHelpfulSuggestion || suggestionPayload;
            // Store suggestions based on context
            if (specificHeadingLevel) {
              // For heading-specific suggestions, store by level
              setLoadedHeadingSuggestions((prev) => ({
                ...prev,
                [specificHeadingLevel]: resolvedSuggestion,
              }));
            } else {
              // For global suggestions (title, meta, helpful content)
              setAiSuggestions(resolvedSuggestion);
            }
            return true;
          }
        } catch (err) {
          if (err.response?.status === 404) {
            retries++;

            if (retries < maxRetries) {
              // Wait before next attempt
              await new Promise((resolve) => setTimeout(resolve, delayMs));
              return poll();
            } else {
              toast.warning(
                "Generation is taking longer than expected. Check browser console for details.",
              );
              return false;
            }
          } else {
            toast.error(
              "Error checking suggestion status: " +
                (err.response?.data?.message || err.message),
            );
            return false;
          }
        }
      };

      return poll();
    },
    [endpointId],
  );

  const generateAiSuggestions = useCallback(
    async (specificHeadingLevel = null) => {
      if (!currentPageId) {
        toast.error("Page ID not found");
        return;
      }

      setGeneratingSuggestions(true);

      // Define url/flags outside try block so they're accessible in catch
      let url = "";
      let isHelpfulContent = false;

      try {
        const endpointLower = (endpointId || "")
          .toLowerCase()
          .replace(/\s+/g, "_");

        const endpointMap = {
          seo_content: "title",
          seo_content_analysis: "title",
          title: "title",
          title_tag: "title",
          meta_description: "meta",
          meta: "meta",
          headings: specificHeadingLevel || "h1", // Use specific level if provided
          heading: specificHeadingLevel || "h1",
          h1: "h1",
          h2: "h2",
          h3: "h3",
          h4: "h4",
          content: "content",
          content_quality: "content",
          readability: "content",
          images: "images",
          image_analysis: "images",
          internal_links: "links",
          links: "links",
          link_analysis: "links",
          helpful_content: "helpful_content",
        };

        const backendEndpoint = endpointMap[endpointLower] || endpointLower;
        isHelpfulContent = endpointLower === "helpful_content";

        toast.info("Generating AI suggestions... This may take a few seconds.");

        // Call backend to generate suggestions
        // Backend endpoint list does NOT include helpful_content; use content for helpful content generation
        const generateEndpoint = isHelpfulContent ? "content" : backendEndpoint;
        url = `/ai-suggestions/generate/${generateEndpoint}/`;
        const payload = {
          page_id: currentPageId,
        };

        // console.log('Sending AI generation request:', { url, payload });
        const response = await axios.post(url, payload);

        // Handle 202 Accepted (async processing) - most common for headings
        if (response.status === 202) {
          toast.info("AI generation in progress, polling for results...");
          const success = await pollForSuggestions(
            currentPageId,
            36,
            3000,
            specificHeadingLevel,
          );
          setSuggestionAttempted(true);

          if (success) {
            toast.success("AI suggestions generated successfully!");
          } else {
            toast.error(
              "Generation took too long. Please try again or check server logs for errors.",
            );
          }
          setGeneratingSuggestions(false);
        } else if (response.data && response.data.status === "success") {
          // For Helpful Content, extract the content suggestion only
          const suggestionPayload = isHelpfulContent
            ? response.data
            : response.data.suggestion ||
              response.data.suggestions ||
              response.data;

          const extractedHelpfulSuggestion =
            isHelpfulContent && suggestionPayload?.suggestions
              ? suggestionPayload.suggestions.find(
                  (s) => (s?.type || "").toLowerCase() === "content",
                )
              : null;

          if (isHelpfulContent && !extractedHelpfulSuggestion) {
            setAiSuggestions(null);
            setSuggestionAttempted(true);
            toast.warning("Helpful content suggestions are not available yet.");
            setGeneratingSuggestions(false);
            return;
          }

          const resolvedSuggestion =
            extractedHelpfulSuggestion || suggestionPayload;

          // Store suggestions based on context
          if (specificHeadingLevel) {
            setLoadedHeadingSuggestions((prev) => ({
              ...prev,
              [specificHeadingLevel]: resolvedSuggestion,
            }));
          } else {
            setAiSuggestions(resolvedSuggestion);
          }
          setSuggestionAttempted(true);
          toast.success("AI suggestions generated successfully!");
          setGeneratingSuggestions(false);
        } else if (
          response.status === 201 ||
          (response.data && response.data.status === "processing")
        ) {
          // Backend is processing asynchronously, poll for results
          toast.info(
            response.data?.message ||
              "AI generation in progress, polling for results...",
          );

          // Poll for suggestions (up to 36 attempts × 3 seconds = 108 seconds)
          const success = await pollForSuggestions(
            currentPageId,
            36,
            3000,
            specificHeadingLevel,
          );
          setSuggestionAttempted(true);

          if (success) {
            toast.success("AI suggestions generated successfully!");
          } else {
            toast.error(
              "Generation took too long. Please try again or check server logs for errors.",
            );
          }
          setGeneratingSuggestions(false);
        } else {
          toast.error("Failed to generate suggestions");
          setSuggestionAttempted(true);
          setGeneratingSuggestions(false);
        }
      } catch (err) {
        const isNetworkError = err.message === "Network Error" || !err.response;
        const errorMessage =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.response?.data?.error ||
          JSON.stringify(err.response?.data) ||
          err.message;

        // Provide specific error messages
        if (err.response?.status === 400) {
          toast.error(`Invalid request: ${errorMessage}`);
        } else if (err.response?.status === 500) {
          toast.error(
            `Backend error: ${errorMessage || "Internal server error (500). Please try again later."}`,
          );
        } else if (isNetworkError) {
          toast.error(
            "Network error - CORS or connection issue. Check browser console.",
          );
        } else {
          toast.error(errorMessage || "Failed to generate AI suggestions");
        }

        // console.error('AI Suggestion Error Details:', {
        //   status: err.response?.status,
        //   data: err.response?.data,
        //   message: err.message
        // });
        setSuggestionAttempted(true);
        setGeneratingSuggestions(false);
      }
    },
    [currentPageId, endpointId, pollForSuggestions],
  );

  useEffect(() => {
    setAiSuggestions(null);
    setSuggestionAttempted(false);
    setLoadingSuggestions(false);
    setLoadedHeadingSuggestions({});
    setSelectedHeadingSuggestions({});
    setSelectedSuggestion(null);

    const loadEndpoint = async () => {
      setLoading(true);
      setError(null);

      if (state?.endpoint && state?.page) {
        // Calculate score from endpoint data
        let calculatedScore = state.endpoint.score;

        if (!calculatedScore || calculatedScore === 0) {
          // Try to calculate score if not provided or is 0
          if (state.endpoint.name && state.endpoint.data) {
            const endpointKey = state.endpoint.name
              .toLowerCase()
              .replace(/\s+/g, "_");
            calculatedScore = calculateScore(endpointKey, state.endpoint.data);
          }
        }

        setEndpoint({
          id: decodeURIComponent(endpointId),
          name: state.endpoint.name,
          score: calculatedScore,
          status: state.endpoint.status,
          data: state.endpoint.data,
          page_url: state.page.page_url,
        });

        // ✅ Use MongoDB ObjectId for AI suggestions, numeric page_id for WordPress auto-implementation
        const pageIdToUse = state.page._id || state.page.page_id;
        const rawImplementationPageId =
          state.page.wordpress_post_id || state.page.page_id;
        const hasNumericImplementationId =
          rawImplementationPageId !== undefined &&
          rawImplementationPageId !== null &&
          /^\d+$/.test(String(rawImplementationPageId));
        const implementationPageIdToUse = hasNumericImplementationId
          ? rawImplementationPageId
          : null;

        setCurrentPageId(pageIdToUse);
        setImplementationPageId(implementationPageIdToUse);
        setLoading(false);

        // Load implementations only when numeric page_id is available; AI suggestions use MongoDB ObjectId
        if (implementationPageIdToUse) {
          loadImplementations(implementationPageIdToUse);
        }

        // Do NOT auto-load AI suggestions; only fetch when user clicks Generate
      } else {
        setEndpoint(null);
        setError("No endpoint data available");
        setLoading(false);
      }
    };

    loadEndpoint();
    checkPluginConnection();
  }, [
    state,
    endpointId,
    loadImplementations,
    loadAiSuggestions,
    checkPluginConnection,
  ]);

  const handleImplementation = async (suggestion) => {
    // Store the suggestion for later use
    setPendingSuggestion(suggestion);
    setImplementing(true);

    // If plugin is already connected, proceed with implementation
    if (pluginConnected) {
      await proceedWithImplementation(suggestion);
    } else {
      // Ask if it's a WordPress website
      setShowWordPressModal(true);
      setImplementing(false);
    }
  };

  const proceedWithImplementation = async (suggestion) => {
    if (!implementationPageId) {
      toast.error("WordPress page ID not found for auto implementation");
      setImplementing(false);
      return;
    }

    setImplementingId(suggestion);

    let pageUrl = "";
    let endpointName = "title";
    let sendImplementationRequest = null;

    try {
      const endpointMap = {
        seo_content: "title",
        title: "title",
        headings: "h1",
        readability: "content",
        keywords: "content",
      };

      endpointName = endpointMap[endpointId] || "title";

      // Get the page URL from endpoint/page state (prefer original page.url)
      pageUrl = state?.page?.url || endpoint?.page_url || state?.page?.page_url;
      if (!pageUrl) {
        toast.error(
          "Page URL is missing. Please reopen the page from SEO results and try again.",
        );
        setImplementingId(null);
        setImplementing(false);
        return;
      }

      // Use MongoDB _id when available so backend resolves URL to WordPress post_id
      // Avoids sending incorrect numeric page_id from frontend
      const mongoPageId = state?.page?._id || currentPageId;
      let wordPressPostId = mongoPageId || implementationPageId;

      sendImplementationRequest = async ({
        pageIdOverride,
        urlOverride,
      } = {}) => {
        const requestPayload = {
          project_id: projectId,
          page_id: pageIdOverride ?? wordPressPostId,
          suggestion: suggestion,
          url: urlOverride ?? pageUrl,
        };

        // console.log('Implementation request:', {
        //   project_id: requestPayload.project_id,
        //   page_id: requestPayload.page_id,
        //   suggestion: requestPayload.suggestion,
        //   url: requestPayload.url,
        //   endpoint: endpointName
        // });

        return axios.post(
          `/auto-implementation/suggestion-implementation/implement-${endpointName}/`,
          requestPayload,
        );
      };

      const response = await sendImplementationRequest();

      if (response.data.success) {
        toast.success(
          "Implementation successful! Refresh the page to see the changes.",
        );
        if (response.data.backup_id) {
          setLastImplementation({
            backupId: response.data.backup_id,
            implementedText: suggestion,
            implementedAt: new Date().toISOString(),
            pageId: wordPressPostId,
          });
        }
        // Reload implementations
        loadImplementations(implementationPageId);
        // Clear selection
        setSelectedSuggestion(null);
      } else {
        toast.error(response.data.error || "Implementation failed");
      }
    } catch (err) {
      // console.error('Error implementing suggestion:', err);
      // console.error('Error response:', err.response?.data);
      const errorCode = err.response?.data?.error_code;
      const errorMessage = err.response?.data?.error || "";

      if (
        errorCode === "no_valid_postid" ||
        errorMessage.includes("Could not resolve page_id")
      ) {
        if (!sendImplementationRequest) {
          toast.error(
            "Failed to prepare implementation request. Please refresh and try again.",
          );
          return;
        }
        const manualUrl = window.prompt(
          "WordPress URL could not be resolved.\n\nPlease enter the correct WordPress page URL to retry:",
          pageUrl,
        );

        if (manualUrl && manualUrl.trim()) {
          try {
            const retryResponse = await sendImplementationRequest({
              pageIdOverride: "url",
              urlOverride: manualUrl.trim(),
            });

            if (retryResponse.data.success) {
              toast.success(
                "Implementation successful! Refresh the page to see the changes.",
              );
              if (retryResponse.data.backup_id) {
                setLastImplementation({
                  backupId: retryResponse.data.backup_id,
                  implementedText: suggestion,
                  implementedAt: new Date().toISOString(),
                  pageUrl: manualUrl.trim(),
                });
              }
              // Reload implementations
              loadImplementations(implementationPageId);
              // Clear selection
              setSelectedSuggestion(null);
              return;
            }

            toast.error(retryResponse.data.error || "Implementation failed");
            return;
          } catch (retryErr) {
            // console.error('Error retrying implementation with manual URL:', retryErr);
            toast.error(
              retryErr.response?.data?.error ||
                "Failed to implement suggestion",
            );
            return;
          }
        }
      }

      if (errorCode === "not_found") {
        toast.error(
          "Endpoint not found on WordPress. Ensure the WordPress plugin is installed, active, and up to date for this site.",
        );
      } else {
        toast.error(
          err.response?.data?.error || "Failed to implement suggestion",
        );
      }
    } finally {
      setImplementingId(null);
      setImplementing(false);
    }
  };

  const handleWordPressYes = () => {
    setShowWordPressModal(false);
    // Navigate to plugin connection page with projectId
    navigate("/dashboard/plugin-connection", { state: { projectId } });
  };

  const handleWordPressNo = () => {
    setShowWordPressModal(false);
    setPendingSuggestion(null);
    toast.info("Auto implementation is only available for WordPress websites");
  };

  const handleRollback = async (backupId, suggestionText) => {
    if (!projectId) {
      toast.error("Project ID not found");
      return;
    }

    setRollingBackId(backupId);

    try {
      const response = await axios.post(
        "/auto-implementation/suggestion-implementation/rollback/",
        {
          project_id: projectId,
          backup_id: backupId,
        },
      );

      if (response.data.success) {
        toast.success("Undo successful! Refresh the page to see the changes.");
        setLastImplementation((prev) => {
          if (!prev || prev.backupId !== backupId) return prev;
          return { ...prev, undone: true };
        });
        // Reload implementations to update the UI
        loadImplementations(implementationPageId);
      } else {
        toast.error(response.data.error || "Rollback failed");
      }
    } catch (err) {
      // console.error('Error rolling back implementation:', err);
      toast.error(
        err.response?.data?.error || "Failed to rollback implementation",
      );
    } finally {
      setRollingBackId(null);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return isDarkMode ? "bg-green-900/30" : "bg-green-50";
    if (score >= 60) return isDarkMode ? "bg-yellow-900/30" : "bg-yellow-50";
    return isDarkMode ? "bg-red-900/30" : "bg-red-50";
  };

  // Recalculate endpoint score using proper calculator if needed
  const formatLabel = (label) => label.replace(/_/g, " ");

  const formatValue = (value) => {
    if (value === null || value === undefined) return "N/A";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "object") return JSON.stringify(value, null, 2);
    return value;
  };

  const isHeadingData = (data) => {
    if (typeof data !== "object" || Array.isArray(data)) return false;

    // Check if data has a nested 'data' property with heading structure
    if (data.data && typeof data.data === "object") {
      const keys = Object.keys(data.data);
      return keys.some((key) => /^h[1-6]$/.test(key));
    }

    // Check if data itself has heading structure
    const keys = Object.keys(data);
    return keys.some((key) => /^h[1-6]$/.test(key));
  };

  const isHelpfulContentData = (data) => {
    if (typeof data !== "object" || Array.isArray(data)) return false;

    // Check if this data contains heading (h1-h6), title, or meta sections - exclude them
    const keys = Object.keys(data);
    const hasHeadingKeys = keys.some(
      (key) =>
        /^h[1-6]$/i.test(key.toLowerCase()) ||
        key.toLowerCase().includes("heading") ||
        key.toLowerCase() === "title" ||
        key.toLowerCase() === "title tag" ||
        key.toLowerCase() === "meta" ||
        key.toLowerCase() === "meta description",
    );

    if (hasHeadingKeys) {
      return false;
    }

    // Check for helpful content structure (sections with questions that have rating/feedback)
    const values = Object.values(data);
    const isHelpfulContent = values.some((val) => {
      if (typeof val === "object" && !Array.isArray(val)) {
        const subValues = Object.values(val);
        return subValues.some((subVal) => {
          // Check if it's already an object with rating and feedback
          if (
            typeof subVal === "object" &&
            !Array.isArray(subVal) &&
            subVal !== null
          ) {
            return subVal.rating !== undefined && subVal.feedback !== undefined;
          }
          // Check if it's a string that parses to rating/feedback
          if (typeof subVal === "string") {
            try {
              const parsed = JSON.parse(subVal);
              return (
                parsed.rating !== undefined && parsed.feedback !== undefined
              );
            } catch {
              return false;
            }
          }
          return false;
        });
      }
      return false;
    });

    return isHelpfulContent;
  };

  const isSeoContentAnalysis = (data) => {
    if (typeof data !== "object" || Array.isArray(data)) return false;

    // Check if "Feedback details" exists at top level (Title Tag, Meta Description, etc.)
    if (data["Feedback details"] !== undefined) {
      return true;
    }

    // Check for SEO content analysis structure (sections with "Feedback details")
    const values = Object.values(data);
    const isSeoContent = values.some((val) => {
      if (typeof val === "object" && !Array.isArray(val)) {
        return val["Feedback details"] !== undefined;
      }
      return false;
    });

    return isSeoContent;
  };

  const renderSeoContentAnalysis = (data) => {
    const excludedSections = new Set([
      "Image analysis",
      "Meta description",
      "Word count",
      "Keyword density",
      "Title tag",
      "On page links summary",
      "Feedback details",
    ]);

    const getFeedbackClass = (className) => {
      switch (className) {
        case "positive":
          return {
            icon: "✓",
            bg: isDarkMode
              ? "bg-green-900/30 border-green-600"
              : "bg-green-50 border-green-400",
            text: isDarkMode ? "text-green-300" : "text-green-800",
            iconBg: "bg-green-500",
          };
        case "negative":
        case "critical":
          return {
            icon: "✗",
            bg: isDarkMode
              ? "bg-red-900/30 border-red-600"
              : "bg-red-50 border-red-400",
            text: isDarkMode ? "text-red-300" : "text-red-800",
            iconBg: "bg-red-500",
          };
        case "warning":
          return {
            icon: "!",
            bg: isDarkMode
              ? "bg-yellow-900/30 border-yellow-600"
              : "bg-yellow-50 border-yellow-400",
            text: isDarkMode ? "text-yellow-300" : "text-yellow-800",
            iconBg: "bg-yellow-500",
          };
        default:
          return {
            icon: "i",
            bg: isDarkMode
              ? "bg-blue-900/30 border-blue-600"
              : "bg-blue-50 border-blue-400",
            text: isDarkMode ? "text-blue-300" : "text-blue-800",
            iconBg: "bg-blue-500",
          };
      }
    };

    const parseFeedbackItem = (item) => {
      if (typeof item === "string") {
        try {
          return JSON.parse(item);
        } catch {
          return null;
        }
      }
      return item;
    };

    const sections = Object.entries(data).filter(
      ([sectionName]) => !excludedSections.has(sectionName),
    );

    return (
      <div className="space-y-6">
        {sections.map(([sectionName, sectionData], sectionIdx) => {
          // Handle nested objects with Feedback details
          if (typeof sectionData === "object" && !Array.isArray(sectionData)) {
            const otherEntries = Object.entries(sectionData).filter(
              ([key]) => key !== "Feedback details",
            );

            return (
              <div
                key={sectionIdx}
                className={`p-4 rounded-lg border ${isDarkMode ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-white"}`}
              >
                <h3
                  className={`text-lg font-semibold mb-4 pb-2 border-b ${isDarkMode ? "text-white border-gray-700" : "text-gray-900 border-gray-200"}`}
                >
                  {formatLabel(sectionName)}
                </h3>

                {/* Display other data fields in grid */}
                {otherEntries.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {otherEntries.map(([key, value]) => (
                      <div
                        key={key}
                        className={`p-3 rounded border ${isDarkMode ? "border-gray-700 bg-gray-700/30" : "border-gray-200 bg-gray-50"}`}
                      >
                        <p
                          className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {formatLabel(key)}
                        </p>
                        <p
                          className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-800"} break-words`}
                        >
                          {typeof value === "object" && !Array.isArray(value)
                            ? JSON.stringify(value)
                            : formatValue(value)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          // Handle primitive values
          return (
            <div
              key={sectionIdx}
              className={`p-3 rounded border ${isDarkMode ? "border-gray-700 bg-gray-700/30" : "border-gray-200 bg-gray-50"}`}
            >
              <p
                className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {formatLabel(sectionName)}
              </p>
              <p
                className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-800"} break-words`}
              >
                {formatValue(sectionData)}
              </p>
            </div>
          );
        })}
      </div>
    );
  };

  const renderHelpfulContentData = (data) => {
    const getRatingColor = (rating) => {
      if (rating >= 8)
        return {
          bg: "bg-green-500",
          bgDark: isDarkMode ? "bg-green-600" : "bg-green-500",
          text: "text-green-500",
          label: "Excellent",
          labelBg: "bg-green-100",
          labelText: "text-green-800",
          priority: 5,
        };
      if (rating >= 6)
        return {
          bg: "bg-blue-500",
          bgDark: isDarkMode ? "bg-blue-600" : "bg-blue-500",
          text: "text-blue-500",
          label: "Good",
          labelBg: "bg-blue-100",
          labelText: "text-blue-800",
          priority: 4,
        };
      if (rating >= 4)
        return {
          bg: "bg-yellow-500",
          bgDark: isDarkMode ? "bg-yellow-600" : "bg-yellow-500",
          text: "text-yellow-500",
          label: "Fair",
          labelBg: "bg-yellow-100",
          labelText: "text-yellow-800",
          priority: 3,
        };
      if (rating >= 2)
        return {
          bg: "bg-orange-500",
          bgDark: isDarkMode ? "bg-orange-600" : "bg-orange-500",
          text: "text-orange-500",
          label: "Poor",
          labelBg: "bg-orange-100",
          labelText: "text-orange-800",
          priority: 2,
        };
      return {
        bg: "bg-red-500",
        bgDark: isDarkMode ? "bg-red-600" : "bg-red-500",
        text: "text-red-500",
        label: "Critical",
        labelBg: "bg-red-100",
        labelText: "text-red-800",
        priority: 1,
      };
    };

    const sections = Object.entries(data);

    const normalizeKey = (value) =>
      value.toString().toLowerCase().replace(/[-_]/g, " ").trim();

    const categorizeSection = (sectionName) => {
      const normalized = normalizeKey(sectionName);
      if (
        normalized.includes("people first") ||
        normalized.includes("people-first")
      )
        return "peopleFirst";
      if (normalized.includes("expertise")) return "expertise";
      if (normalized.includes("content") || normalized.includes("quality"))
        return "contentQuality";
      return "contentQuality";
    };

    const groupedSections = sections.reduce(
      (acc, section) => {
        const groupKey = categorizeSection(section[0]);
        acc[groupKey].push(section);
        return acc;
      },
      { contentQuality: [], expertise: [], peopleFirst: [] },
    );

    const renderSectionCards = (groupSections) => (
      <div className="space-y-6">
        {groupSections.map(([sectionName, questions], sectionIdx) => {
          if (typeof questions !== "object" || Array.isArray(questions))
            return null;

          const questionEntries = Object.entries(questions)
            // Sort by priority: Critical/Poor first, then Fair, Good, Excellent
            .sort((a, b) => {
              const aRating =
                typeof a[1] === "string"
                  ? JSON.parse(a[1]).rating
                  : a[1].rating;
              const bRating =
                typeof b[1] === "string"
                  ? JSON.parse(b[1]).rating
                  : b[1].rating;
              const aPriority = getRatingColor(aRating).priority;
              const bPriority = getRatingColor(bRating).priority;
              return aPriority - bPriority; // Lower priority number = more critical = show first
            });

          return (
            <div
              key={sectionIdx}
              className={`p-4 rounded-lg border ${isDarkMode ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-white"}`}
            >
              <h3
                className={`text-lg font-semibold mb-4 pb-2 border-b ${isDarkMode ? "text-white border-gray-700" : "text-gray-900 border-gray-200"}`}
              >
                {formatLabel(sectionName)}
              </h3>

              <div className="space-y-4">
                {questionEntries.map(([question, answerStr], qIdx) => {
                  let answer;
                  try {
                    // Handle both string and object formats
                    answer =
                      typeof answerStr === "string"
                        ? JSON.parse(answerStr)
                        : answerStr;
                  } catch {
                    return null;
                  }

                  if (!answer || !answer.rating || !answer.feedback)
                    return null;

                  const ratingColor = getRatingColor(answer.rating);
                  const percentage = (answer.rating / 10) * 100;

                  const questionKey = `${sectionIdx}-${qIdx}`;
                  const isExpanded =
                    expandedHelpfulQuestions[questionKey] || false;

                  return (
                    <div
                      key={qIdx}
                      className={`rounded-lg border-2 overflow-hidden ${isDarkMode ? "border-gray-700 bg-gray-700/30" : "border-gray-100 bg-gray-50"}`}
                    >
                      {/* Question Header - Clickable */}
                      <button
                        onClick={() =>
                          setExpandedHelpfulQuestions((prev) => ({
                            ...prev,
                            [questionKey]: !isExpanded,
                          }))
                        }
                        className={`w-full p-4 flex items-center justify-between transition-colors ${isDarkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-100"}`}
                      >
                        <div className="flex-1 text-left">
                          <p
                            className={`font-medium text-sm ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                          >
                            {question.replace(/:\s*$/, "")}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-bold ${ratingColor.labelBg} ${ratingColor.labelText}`}
                          >
                            {ratingColor.label}
                          </span>
                          {isExpanded ? (
                            <FaChevronUp
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }
                              size={14}
                            />
                          ) : (
                            <FaChevronDown
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }
                              size={14}
                            />
                          )}
                        </div>
                      </button>

                      {/* Expandable Content */}
                      {isExpanded && (
                        <div className="p-4 pt-0">
                          {/* Rating Section */}
                          <div
                            className="flex items-center gap-3 mb-4 p-3 rounded-lg"
                            style={{
                              backgroundColor: isDarkMode
                                ? "rgba(0,0,0,0.2)"
                                : "rgba(255,255,255,0.6)",
                            }}
                          >
                            <div
                              className={`flex-shrink-0 w-16 h-16 rounded-full flex items-center justify-center ${ratingColor.bgDark} text-white shadow-lg`}
                            >
                              <span className="text-2xl font-bold">
                                {answer.rating}
                              </span>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span
                                  className={`text-2xl font-bold ${ratingColor.text}`}
                                >
                                  {answer.rating}/10
                                </span>
                                <span
                                  className={`px-3 py-1 rounded-full text-xs font-bold ${ratingColor.labelBg} ${ratingColor.labelText}`}
                                >
                                  {ratingColor.label}
                                </span>
                              </div>
                              {/* Rating bar */}
                              <div
                                className={`w-full h-2 rounded-full overflow-hidden ${isDarkMode ? "bg-gray-600" : "bg-gray-300"}`}
                              >
                                <div
                                  className={`h-full ${ratingColor.bg} transition-all duration-300`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          </div>

                          {/* Feedback */}
                          <div
                            className={`p-4 rounded-lg border-l-4 ${ratingColor.bg} ${isDarkMode ? "bg-gray-800/50 text-gray-100" : "bg-white text-gray-800"}`}
                          >
                            <p
                              className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                            >
                              Feedback
                            </p>
                            <p className="leading-relaxed text-sm">
                              {answer.feedback}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );

    return (
      <div className="space-y-4">
        <div
          className={`rounded-lg border ${isDarkMode ? "border-blue-700 bg-blue-900/10" : "border-blue-300 bg-blue-50/50"}`}
        >
          <button
            onClick={() => setShowContentQuality(!showContentQuality)}
            className={`w-full p-4 flex items-center justify-between transition-colors ${
              isDarkMode ? "hover:bg-blue-900/20" : "hover:bg-blue-100/50"
            }`}
          >
            <div className="text-left">
              <h4
                className={`text-sm font-semibold ${isDarkMode ? "text-blue-200" : "text-blue-900"}`}
              >
                Content & Quality
              </h4>
              <p
                className={`text-xs ${isDarkMode ? "text-blue-300" : "text-blue-700"}`}
              >
                {groupedSections.contentQuality.length}{" "}
                {groupedSections.contentQuality.length === 1
                  ? "section"
                  : "sections"}
              </p>
            </div>
            {showContentQuality ? (
              <FaChevronUp
                className={isDarkMode ? "text-blue-300" : "text-blue-700"}
              />
            ) : (
              <FaChevronDown
                className={isDarkMode ? "text-blue-300" : "text-blue-700"}
              />
            )}
          </button>
          {showContentQuality && (
            <div className="p-4 pt-0">
              {renderSectionCards(groupedSections.contentQuality)}
            </div>
          )}
        </div>

        <div
          className={`rounded-lg border ${isDarkMode ? "border-purple-700 bg-purple-900/10" : "border-purple-300 bg-purple-50/50"}`}
        >
          <button
            onClick={() => setShowExpertise(!showExpertise)}
            className={`w-full p-4 flex items-center justify-between transition-colors ${
              isDarkMode ? "hover:bg-purple-900/20" : "hover:bg-purple-100/50"
            }`}
          >
            <div className="text-left">
              <h4
                className={`text-sm font-semibold ${isDarkMode ? "text-purple-200" : "text-purple-900"}`}
              >
                Expertise
              </h4>
              <p
                className={`text-xs ${isDarkMode ? "text-purple-300" : "text-purple-700"}`}
              >
                {groupedSections.expertise.length}{" "}
                {groupedSections.expertise.length === 1
                  ? "section"
                  : "sections"}
              </p>
            </div>
            {showExpertise ? (
              <FaChevronUp
                className={isDarkMode ? "text-purple-300" : "text-purple-700"}
              />
            ) : (
              <FaChevronDown
                className={isDarkMode ? "text-purple-300" : "text-purple-700"}
              />
            )}
          </button>
          {showExpertise && (
            <div className="p-4 pt-0">
              {renderSectionCards(groupedSections.expertise)}
            </div>
          )}
        </div>

        <div
          className={`rounded-lg border ${isDarkMode ? "border-green-700 bg-green-900/10" : "border-green-300 bg-green-50/50"}`}
        >
          <button
            onClick={() => setShowPeopleFirst(!showPeopleFirst)}
            className={`w-full p-4 flex items-center justify-between transition-colors ${
              isDarkMode ? "hover:bg-green-900/20" : "hover:bg-green-100/50"
            }`}
          >
            <div className="text-left">
              <h4
                className={`text-sm font-semibold ${isDarkMode ? "text-green-200" : "text-green-900"}`}
              >
                People-first Content
              </h4>
              <p
                className={`text-xs ${isDarkMode ? "text-green-300" : "text-green-700"}`}
              >
                {groupedSections.peopleFirst.length}{" "}
                {groupedSections.peopleFirst.length === 1
                  ? "section"
                  : "sections"}
              </p>
            </div>
            {showPeopleFirst ? (
              <FaChevronUp
                className={isDarkMode ? "text-green-300" : "text-green-700"}
              />
            ) : (
              <FaChevronDown
                className={isDarkMode ? "text-green-300" : "text-green-700"}
              />
            )}
          </button>
          {showPeopleFirst && (
            <div className="p-4 pt-0">
              {renderSectionCards(groupedSections.peopleFirst)}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderHeadingData = (data) => {
    // Extract the actual heading data (might be nested in data.data)
    const headingData = data.data || data;

    const headingLevels = ["h1", "h2", "h3", "h4", "h5", "h6"];
    const headingLabels = {
      h1: { label: "Main Heading (H1)", optimal: 1, color: "blue" },
      h2: { label: "Subheadings (H2)", optimal: "1-3", color: "indigo" },
      h3: {
        label: "Sub-subheadings (H3)",
        optimal: "Multiple",
        color: "purple",
      },
      h4: { label: "Heading Level 4 (H4)", optimal: "Optional", color: "pink" },
      h5: { label: "Heading Level 5 (H5)", optimal: "Optional", color: "rose" },
      h6: {
        label: "Heading Level 6 (H6)",
        optimal: "Optional",
        color: "orange",
      },
    };

    const colorMap = {
      blue: {
        border: "border-blue-600",
        bg: isDarkMode ? "bg-blue-900/20" : "bg-blue-50",
        text: "text-blue-700",
        badge: "bg-blue-100 text-blue-800",
      },
      indigo: {
        border: "border-indigo-600",
        bg: isDarkMode ? "bg-indigo-900/20" : "bg-indigo-50",
        text: "text-indigo-700",
        badge: "bg-indigo-100 text-indigo-800",
      },
      purple: {
        border: "border-purple-600",
        bg: isDarkMode ? "bg-purple-900/20" : "bg-purple-50",
        text: "text-purple-700",
        badge: "bg-purple-100 text-purple-800",
      },
      pink: {
        border: "border-pink-600",
        bg: isDarkMode ? "bg-pink-900/20" : "bg-pink-50",
        text: "text-pink-700",
        badge: "bg-pink-100 text-pink-800",
      },
      rose: {
        border: "border-rose-600",
        bg: isDarkMode ? "bg-rose-900/20" : "bg-rose-50",
        text: "text-rose-700",
        badge: "bg-rose-100 text-rose-800",
      },
      orange: {
        border: "border-orange-600",
        bg: isDarkMode ? "bg-orange-900/20" : "bg-orange-50",
        text: "text-orange-700",
        badge: "bg-orange-100 text-orange-800",
      },
    };

    return (
      <div className="space-y-4">
        {headingLevels.map((level) => {
          const headings = headingData[level] || [];
          const config = headingLabels[level];
          const colors = colorMap[config.color];
          const isEmpty = headings.length === 0;
          const isOptimal = level === "h1" ? headings.length === 1 : true;
          const isExpanded =
            expandedHeadings[level] ?? (level === "h1" || level === "h2");

          const toggleHeading = () => {
            setExpandedHeadings((prev) => ({
              ...prev,
              [level]: !prev[level],
            }));
          };

          return (
            <div
              key={level}
              className={`border-l-4 rounded-r-lg overflow-hidden ${colors.border} ${colors.bg}`}
            >
              <button onClick={toggleHeading} className="w-full text-left">
                <div
                  className={`p-4 ${isDarkMode ? "bg-opacity-20" : ""} hover:opacity-90 transition-opacity`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3
                          className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {config.label}
                        </h3>
                        <span
                          className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          {isExpanded ? (
                            <FaChevronUp
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }
                            />
                          ) : (
                            <FaChevronDown
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }
                            />
                          )}
                        </span>
                      </div>
                      <p
                        className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                      >
                        Optimal: {config.optimal}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-semibold ${colors.badge}`}
                      >
                        {headings.length}
                      </span>
                      {level === "h1" && (
                        <span
                          className={`px-2 py-1 rounded text-xs font-medium ${
                            isOptimal
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {isOptimal ? "✓ Good" : "✗ Warning"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>

              {isExpanded && (
                <div
                  className={`p-4 pt-0 ${isDarkMode ? "bg-opacity-10" : ""}`}
                >
                  {isEmpty ? (
                    <div
                      className={`p-3 rounded text-sm text-center ${isDarkMode ? "bg-gray-700/30 text-gray-400" : "bg-gray-100 text-gray-600"}`}
                    >
                      No {config.label.toLowerCase()} found on this page
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {(() => {
                        const levelSuggestions =
                          loadedHeadingSuggestions[level];
                        const hasLevelSuggestions = !!(
                          levelSuggestions &&
                          ((levelSuggestions.items &&
                            levelSuggestions.items.length > 0) ||
                            (levelSuggestions.variations &&
                              levelSuggestions.variations.length > 0))
                        );
                        const canGenerate =
                          level === "h1" ||
                          level === "h2" ||
                          level === "h3" ||
                          level === "h4";

                        return !hasLevelSuggestions && canGenerate ? (
                          <div className="flex flex-col items-center gap-3 p-4 rounded border border-dashed">
                            <p
                              className={`text-sm text-center ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                            >
                              Click below to generate AI suggestions for{" "}
                              {config.label.toLowerCase()}
                            </p>
                            <button
                              onClick={async () => {
                                setGeneratingHeadingSuggestions((prev) => ({
                                  ...prev,
                                  [level]: true,
                                }));
                                await generateAiSuggestions(level); // Pass the specific heading level
                                setGeneratingHeadingSuggestions((prev) => ({
                                  ...prev,
                                  [level]: false,
                                }));
                              }}
                              disabled={generatingHeadingSuggestions[level]}
                              className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-500 text-white rounded font-semibold text-sm transition-colors"
                            >
                              {generatingHeadingSuggestions[level] ? (
                                <span className="flex items-center gap-2">
                                  <FaSpinner className="animate-spin" />
                                  Generating...
                                </span>
                              ) : (
                                "Generate AI Insights"
                              )}
                            </button>
                          </div>
                        ) : null;
                      })()}
                      {headings.map((heading, idx) => {
                        const isH5orH6 = level === "h5" || level === "h6";

                        if (isH5orH6) {
                          // For H5/H6: Show heading text without dropdown button
                          return (
                            <div key={idx} className="space-y-2">
                              <div
                                className={`p-3 rounded border-l-2 ${
                                  isDarkMode
                                    ? "bg-gray-800/50 border-l-gray-600"
                                    : "bg-white/50 border-l-gray-300"
                                }`}
                              >
                                <p
                                  className={`text-sm break-words ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                                >
                                  {heading || (
                                    <span
                                      className={
                                        isDarkMode
                                          ? "text-gray-500 italic"
                                          : "text-gray-400 italic"
                                      }
                                    >
                                      Empty heading
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                          );
                        }

                        // For H1-H4: Show with dropdown button
                        return (
                          <div key={idx} className="space-y-2">
                            <button
                              onClick={() => {
                                setExpandedKeywords((prev) => ({
                                  ...prev,
                                  [`${level}-${idx}`]: !prev[`${level}-${idx}`],
                                }));
                              }}
                              className={`w-full p-3 rounded border-l-2 flex items-center justify-between transition-colors ${
                                isDarkMode
                                  ? "bg-gray-800/50 border-l-gray-600 hover:bg-gray-800/70"
                                  : "bg-white/50 border-l-gray-300 hover:bg-white/70"
                              }`}
                            >
                              <div className="flex-1 text-left">
                                <p
                                  className={`text-sm break-words ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                                >
                                  {heading || (
                                    <span
                                      className={
                                        isDarkMode
                                          ? "text-gray-500 italic"
                                          : "text-gray-400 italic"
                                      }
                                    >
                                      Empty heading
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 ml-2">
                                {loadedHeadingSuggestions[level] &&
                                  (() => {
                                    const levelSuggestions =
                                      loadedHeadingSuggestions[level];
                                    let suggestionCount = 0;
                                    if (levelSuggestions?.items) {
                                      const matchingItems =
                                        levelSuggestions.items.filter(
                                          (item) => {
                                            const currentText = (
                                              item.current ||
                                              item.current_text ||
                                              ""
                                            )
                                              .toLowerCase()
                                              .trim();
                                            const headingText = (heading || "")
                                              .toLowerCase()
                                              .trim();
                                            return currentText === headingText;
                                          },
                                        );
                                      suggestionCount = matchingItems.reduce(
                                        (sum, item) =>
                                          sum + (item.variations?.length || 0),
                                        0,
                                      );
                                    } else if (levelSuggestions?.variations) {
                                      suggestionCount =
                                        levelSuggestions.variations.length;
                                    }
                                    return suggestionCount > 0 ? (
                                      <span
                                        className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
                                          isDarkMode
                                            ? "bg-green-700/30 text-green-300"
                                            : "bg-green-100 text-green-800"
                                        }`}
                                      >
                                        {suggestionCount} suggestion(s)
                                      </span>
                                    ) : null;
                                  })()}
                                <span
                                  className={`flex-shrink-0 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                >
                                  {expandedKeywords[`${level}-${idx}`] ? (
                                    <FaChevronUp size={14} />
                                  ) : (
                                    <FaChevronDown size={14} />
                                  )}
                                </span>
                              </div>
                            </button>

                            {expandedKeywords[`${level}-${idx}`] && (
                              <div
                                className={`ml-2 p-4 rounded border-l-2 space-y-3 ${
                                  isDarkMode
                                    ? "bg-gray-900/50 border-l-green-600"
                                    : "bg-green-50/50 border-l-green-600"
                                }`}
                              >
                                {!loadedHeadingSuggestions[level] ? (
                                  <div
                                    className={`p-3 rounded text-sm text-center ${isDarkMode ? "bg-gray-800/50 text-gray-400" : "bg-white/50 text-gray-600"}`}
                                  >
                                    Generate AI insights above to view
                                    suggestions for this heading.
                                  </div>
                                ) : (
                                  <>
                                    {(() => {
                                      const levelSuggestions =
                                        loadedHeadingSuggestions[level];
                                      const headingText = (heading || "")
                                        .toLowerCase()
                                        .trim();

                                      // Find matching items for this heading
                                      let matchingItems =
                                        levelSuggestions?.items?.filter(
                                          (item) => {
                                            const currentText = (
                                              item.current ||
                                              item.current_text ||
                                              ""
                                            )
                                              .toLowerCase()
                                              .trim();
                                            return currentText === headingText;
                                          },
                                        ) || [];

                                      // Fallback: if no exact match, use first item (common backend behavior)
                                      if (
                                        matchingItems.length === 0 &&
                                        levelSuggestions?.items?.length === 1
                                      ) {
                                        matchingItems = levelSuggestions.items;
                                      }

                                      const hasMatchingData =
                                        matchingItems.length > 0 ||
                                        levelSuggestions?.variations?.length >
                                          0;

                                      if (!hasMatchingData) {
                                        return (
                                          <div
                                            className={`p-3 rounded text-sm text-center ${isDarkMode ? "bg-gray-800/50 text-gray-400" : "bg-white/50 text-gray-600"}`}
                                          >
                                            No suggestions available for this
                                            heading
                                          </div>
                                        );
                                      }

                                      return (
                                        <>
                                          {/* Issues First - Red Styling */}
                                          {matchingItems.some(
                                            (item) =>
                                              item.issues &&
                                              item.issues.length > 0,
                                          ) && (
                                            <div
                                              className={`mb-3 p-3 rounded-lg border-2 border-l-4 ${isDarkMode ? "border-red-700 bg-red-900/20" : "border-red-300 bg-red-50"}`}
                                            >
                                              <p
                                                className={`text-xs font-bold mb-2 flex items-center gap-2 ${isDarkMode ? "text-red-300" : "text-red-800"}`}
                                              >
                                                <span>⚠️</span>
                                                Issues Found:
                                              </p>
                                              {matchingItems.flatMap((item) =>
                                                (item.issues || []).map(
                                                  (issue, iIdx) => (
                                                    <p
                                                      key={iIdx}
                                                      className={`text-xs p-2 rounded mb-1 ${isDarkMode ? "bg-red-900/30 text-red-200" : "bg-red-100 text-red-800"}`}
                                                    >
                                                      • {issue.detail || issue}
                                                    </p>
                                                  ),
                                                ),
                                              )}
                                            </div>
                                          )}

                                          {/* AI Suggestions Header */}
                                          <div
                                            className={`flex items-center gap-2 mb-3 ${isDarkMode ? "text-green-300" : "text-green-700"}`}
                                          >
                                            <span className="text-sm font-semibold flex items-center gap-1">
                                              <span>✨</span>
                                              AI Suggestions
                                            </span>
                                          </div>

                                          {/* Suggested Variations - Green Styling */}
                                          <div className="space-y-2">
                                            {levelSuggestions?.items
                                              ? matchingItems.flatMap(
                                                  (item, itemIdx) =>
                                                    (item.variations || []).map(
                                                      (variation, vIdx) => {
                                                        const suggestionText =
                                                          variation.text ||
                                                          variation.html ||
                                                          variation;
                                                        const suggestionKey = `${level}-${idx}-${itemIdx}-${vIdx}`;
                                                        const isSelected =
                                                          selectedHeadingSuggestions[
                                                            suggestionKey
                                                          ] || false;
                                                        const charCount =
                                                          suggestionText
                                                            ? suggestionText.length
                                                            : 0;

                                                        return (
                                                          <div
                                                            key={suggestionKey}
                                                            className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all shadow-sm ${
                                                              isSelected
                                                                ? isDarkMode
                                                                  ? "border-blue-500 bg-blue-900/40 shadow-blue-500/50"
                                                                  : "border-blue-500 bg-blue-100 shadow-blue-200"
                                                                : isDarkMode
                                                                  ? "border-green-600 bg-green-900/20 hover:border-green-500 hover:shadow-md"
                                                                  : "border-green-400 bg-white hover:border-green-500 hover:shadow-md"
                                                            }`}
                                                            onClick={() => {
                                                              setSelectedHeadingSuggestions(
                                                                (prev) => ({
                                                                  ...prev,
                                                                  [suggestionKey]:
                                                                    !isSelected,
                                                                }),
                                                              );
                                                            }}
                                                          >
                                                            <input
                                                              type="checkbox"
                                                              checked={
                                                                isSelected
                                                              }
                                                              onChange={(e) => {
                                                                setSelectedHeadingSuggestions(
                                                                  (prev) => ({
                                                                    ...prev,
                                                                    [suggestionKey]:
                                                                      e.target
                                                                        .checked,
                                                                  }),
                                                                );
                                                              }}
                                                              className="mt-1 w-5 h-5 rounded cursor-pointer flex-shrink-0 text-green-600 focus:ring-green-500"
                                                              onClick={(e) =>
                                                                e.stopPropagation()
                                                              }
                                                            />
                                                            <div className="flex-1">
                                                              <div className="flex items-center justify-between mb-1">
                                                                <span
                                                                  className={`text-xs font-bold ${isDarkMode ? "text-green-400" : "text-green-700"}`}
                                                                >
                                                                  Option{" "}
                                                                  {vIdx + 1}
                                                                </span>
                                                                <span
                                                                  className={`text-xs font-semibold px-2 py-0.5 rounded ${isDarkMode ? "bg-green-900/50 text-green-300" : "bg-green-200 text-green-700"}`}
                                                                >
                                                                  {charCount}{" "}
                                                                  characters
                                                                </span>
                                                              </div>
                                                              <p
                                                                className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                                                              >
                                                                {suggestionText}
                                                              </p>
                                                            </div>
                                                          </div>
                                                        );
                                                      },
                                                    ),
                                                )
                                              : levelSuggestions?.variations?.map(
                                                  (variation, sidx) => {
                                                    const suggestionText =
                                                      variation.text ||
                                                      variation;
                                                    const suggestionKey = `${level}-${idx}-${sidx}`;
                                                    const isSelected =
                                                      selectedHeadingSuggestions[
                                                        suggestionKey
                                                      ] || false;
                                                    const charCount =
                                                      suggestionText
                                                        ? suggestionText.length
                                                        : 0;

                                                    return (
                                                      <div
                                                        key={sidx}
                                                        className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all shadow-sm ${
                                                          isSelected
                                                            ? isDarkMode
                                                              ? "border-blue-500 bg-blue-900/40 shadow-blue-500/50"
                                                              : "border-blue-500 bg-blue-100 shadow-blue-200"
                                                            : isDarkMode
                                                              ? "border-green-600 bg-green-900/20 hover:border-green-500 hover:shadow-md"
                                                              : "border-green-400 bg-white hover:border-green-500 hover:shadow-md"
                                                        }`}
                                                        onClick={() => {
                                                          setSelectedHeadingSuggestions(
                                                            (prev) => ({
                                                              ...prev,
                                                              [suggestionKey]:
                                                                !isSelected,
                                                            }),
                                                          );
                                                        }}
                                                      >
                                                        <input
                                                          type="checkbox"
                                                          checked={isSelected}
                                                          onChange={(e) => {
                                                            setSelectedHeadingSuggestions(
                                                              (prev) => ({
                                                                ...prev,
                                                                [suggestionKey]:
                                                                  e.target
                                                                    .checked,
                                                              }),
                                                            );
                                                          }}
                                                          className="mt-1 w-5 h-5 rounded cursor-pointer flex-shrink-0 text-green-600 focus:ring-green-500"
                                                          onClick={(e) =>
                                                            e.stopPropagation()
                                                          }
                                                        />
                                                        <div className="flex-1">
                                                          <div className="flex items-center justify-between mb-1">
                                                            <span
                                                              className={`text-xs font-bold ${isDarkMode ? "text-green-400" : "text-green-700"}`}
                                                            >
                                                              Option {sidx + 1}
                                                            </span>
                                                            <span
                                                              className={`text-xs font-semibold px-2 py-0.5 rounded ${isDarkMode ? "bg-green-900/50 text-green-300" : "bg-green-200 text-green-700"}`}
                                                            >
                                                              {charCount}{" "}
                                                              characters
                                                            </span>
                                                          </div>
                                                          <p
                                                            className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                                                          >
                                                            {suggestionText}
                                                          </p>
                                                        </div>
                                                      </div>
                                                    );
                                                  },
                                                )}
                                          </div>
                                        </>
                                      );
                                    })()}

                                    {(() => {
                                      const headingKey = `${level}-${idx}`;
                                      const hasSelectedSuggestions =
                                        Object.entries(
                                          selectedHeadingSuggestions,
                                        ).some(
                                          ([key, value]) =>
                                            key.startsWith(headingKey + "-") &&
                                            value,
                                        );

                                      return (
                                        <button
                                          disabled={!hasSelectedSuggestions}
                                          onClick={() => {
                                            const selectedSuggestions =
                                              Object.entries(
                                                selectedHeadingSuggestions,
                                              )
                                                .filter(
                                                  ([key, value]) =>
                                                    key.startsWith(
                                                      `${level}-${idx}-`,
                                                    ) && value,
                                                )
                                                .map(([key]) => key);

                                            // console.log('Implementing suggestions:', selectedSuggestions);
                                            toast.success(
                                              "Suggestion implemented successfully!",
                                            );

                                            // Clear selections after implementation
                                            setTimeout(() => {
                                              // Clear selected suggestions for this heading
                                              const newSelections = {
                                                ...selectedHeadingSuggestions,
                                              };
                                              Object.keys(
                                                newSelections,
                                              ).forEach((key) => {
                                                if (
                                                  key.startsWith(
                                                    `${level}-${idx}-`,
                                                  )
                                                ) {
                                                  delete newSelections[key];
                                                }
                                              });
                                              setSelectedHeadingSuggestions(
                                                newSelections,
                                              );
                                            }, 500);
                                          }}
                                          className={`w-full mt-3 py-2 px-4 rounded font-semibold text-sm transition-colors ${
                                            hasSelectedSuggestions
                                              ? "bg-green-600 hover:bg-green-700 text-white cursor-pointer"
                                              : isDarkMode
                                                ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                                                : "bg-gray-200 text-gray-400 cursor-not-allowed"
                                          }`}
                                        >
                                          Implement Suggestion
                                        </button>
                                      );
                                    })()}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const isFeedbackDetailsKey = (key) => {
    if (!key) return false;
    return key.toString().toLowerCase().trim() === "feedback details";
  };

  const isKeywordSuggestionData = (data) => {
    if (!Array.isArray(data) || data.length === 0) return false;
    const firstItem = data[0];
    if (typeof firstItem !== "object" || firstItem === null) return false;
    // Check if it has keyword-related fields
    return (
      (firstItem.keyword !== undefined || firstItem.Keyword !== undefined) &&
      (firstItem.competition !== undefined ||
        firstItem.Competition !== undefined ||
        firstItem.cpc !== undefined ||
        firstItem.CPC !== undefined ||
        firstItem["search volume"] !== undefined ||
        firstItem["Search Volume"] !== undefined)
    );
  };

  const renderKeywordSuggestionData = (data) => {
    const toggleKeyword = (index) => {
      setExpandedKeywords((prev) => ({
        ...prev,
        [index]: !prev[index],
      }));
    };

    return (
      <div className="space-y-3">
        {data.map((item, index) => {
          const isExpanded = expandedKeywords[index] || false;
          const keyword = item.keyword || item.Keyword || "N/A";
          const competition = item.competition ?? item.Competition ?? "N/A";
          const cpc = item.cpc ?? item.CPC ?? "N/A";
          const searchVolume =
            item["search volume"] ??
            item["Search Volume"] ??
            item.searchVolume ??
            "N/A";

          return (
            <div
              key={index}
              className={`rounded-lg border ${isDarkMode ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-white"}`}
            >
              <button
                onClick={() => toggleKeyword(index)}
                className={`w-full p-4 flex items-center justify-between transition-colors ${
                  isDarkMode ? "hover:bg-gray-700/50" : "hover:bg-gray-50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`px-2 py-1 rounded text-xs font-bold ${
                      isDarkMode
                        ? "bg-blue-700 text-blue-100"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    #{index + 1}
                  </span>
                  <div className="text-left">
                    <h4
                      className={`text-sm font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                    >
                      {keyword}
                    </h4>
                    {!isExpanded && (
                      <p
                        className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                      >
                        Click to view details
                      </p>
                    )}
                  </div>
                </div>
                {isExpanded ? (
                  <FaChevronUp
                    className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                  />
                ) : (
                  <FaChevronDown
                    className={isDarkMode ? "text-gray-400" : "text-gray-600"}
                  />
                )}
              </button>

              {isExpanded && (
                <div
                  className={`p-4 pt-0 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Competition */}
                    <div
                      className={`p-3 rounded ${isDarkMode ? "bg-gray-700/50" : "bg-gray-50"}`}
                    >
                      <p
                        className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Competition
                      </p>
                      <p
                        className={`text-lg font-bold ${isDarkMode ? "text-yellow-400" : "text-yellow-600"}`}
                      >
                        {typeof competition === "number"
                          ? competition.toFixed(2)
                          : competition}
                      </p>
                    </div>

                    {/* CPC */}
                    <div
                      className={`p-3 rounded ${isDarkMode ? "bg-gray-700/50" : "bg-gray-50"}`}
                    >
                      <p
                        className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        CPC
                      </p>
                      <p
                        className={`text-lg font-bold ${isDarkMode ? "text-green-400" : "text-green-600"}`}
                      >
                        ${typeof cpc === "number" ? cpc.toFixed(2) : cpc}
                      </p>
                    </div>

                    {/* Search Volume */}
                    <div
                      className={`p-3 rounded ${isDarkMode ? "bg-gray-700/50" : "bg-gray-50"}`}
                    >
                      <p
                        className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Search Volume
                      </p>
                      <p
                        className={`text-lg font-bold ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}
                      >
                        {typeof searchVolume === "number"
                          ? searchVolume.toLocaleString()
                          : searchVolume}
                      </p>
                    </div>
                  </div>

                  {/* Additional fields if present */}
                  {Object.entries(item).filter(
                    ([key]) =>
                      ![
                        "keyword",
                        "Keyword",
                        "competition",
                        "Competition",
                        "cpc",
                        "CPC",
                        "search volume",
                        "Search Volume",
                        "searchVolume",
                      ].includes(key),
                  ).length > 0 && (
                    <div
                      className={`mt-3 pt-3 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <p
                        className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Additional Info
                      </p>
                      <div className="space-y-2">
                        {Object.entries(item)
                          .filter(
                            ([key]) =>
                              ![
                                "keyword",
                                "Keyword",
                                "competition",
                                "Competition",
                                "cpc",
                                "CPC",
                                "search volume",
                                "Search Volume",
                                "searchVolume",
                              ].includes(key),
                          )
                          .map(([key, value]) => (
                            <div key={key} className="flex flex-col gap-1">
                              <span
                                className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                              >
                                {formatLabel(key)}
                              </span>
                              <span
                                className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-800"} break-words`}
                              >
                                {formatValue(value)}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderRawDataContent = () => {
    if (!endpoint?.data) return null;

    // Check if data is heading data
    if (isHeadingData(endpoint.data)) {
      return renderHeadingData(endpoint.data);
    }

    // Extract the actual data (might be nested in data.data)
    let actualData = endpoint.data;
    if (endpoint.data.data && typeof endpoint.data.data === "object") {
      actualData = endpoint.data.data;
    }

    // Check detection BEFORE logging to see proper detection
    const detectedSeoContent = isSeoContentAnalysis(actualData);
    const detectedHelpfulContent = isHelpfulContentData(actualData);
    const detectedKeywordSuggestion = isKeywordSuggestionData(actualData);

    // Check if data is SEO content analysis
    if (detectedSeoContent) {
      return renderSeoContentAnalysis(actualData);
    }

    // Check if data is helpful content analysis
    if (detectedHelpfulContent) {
      return renderHelpfulContentData(actualData);
    }

    // Check if data is keyword suggestions
    if (detectedKeywordSuggestion) {
      return renderKeywordSuggestionData(actualData);
    }

    const isObject =
      typeof actualData === "object" && !Array.isArray(actualData);
    const isArray = Array.isArray(actualData);

    // Handle arrays
    if (isArray) {
      if (actualData.length === 0) {
        return (
          <p
            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
          >
            No data available.
          </p>
        );
      }

      // Check if this is PWA audit data (array with summary + nested array of audit items)
      const isPwaAudit =
        actualData.length >= 2 &&
        Array.isArray(actualData[1]) &&
        actualData[1].length > 0 &&
        actualData[1][0]?.Title &&
        actualData[1][0]?.Feedback &&
        actualData[1][0]?.Status;

      if (isPwaAudit) {
        const summaryItem = actualData[0];
        const auditItems = actualData[1];

        // Separate items by status
        const negativeItems = auditItems.filter(
          (item) => item.Status?.toLowerCase() === "negative",
        );
        const positiveItems = auditItems.filter(
          (item) => item.Status?.toLowerCase() === "positive",
        );

        const getStatusColor = (status) => {
          switch (status?.toLowerCase()) {
            case "positive":
              return {
                bg: isDarkMode
                  ? "bg-green-900/30 border-green-600"
                  : "bg-green-50 border-green-300",
                icon: "✓",
                iconBg: "bg-green-500",
                textColor: isDarkMode ? "text-green-300" : "text-green-800",
              };
            case "negative":
              return {
                bg: isDarkMode
                  ? "bg-red-900/30 border-red-600"
                  : "bg-red-50 border-red-300",
                icon: "✕",
                iconBg: "bg-red-500",
                textColor: isDarkMode ? "text-red-300" : "text-red-800",
              };
            case "warning":
              return {
                bg: isDarkMode
                  ? "bg-yellow-900/30 border-yellow-600"
                  : "bg-yellow-50 border-yellow-300",
                icon: "!",
                iconBg: "bg-yellow-500",
                textColor: isDarkMode ? "text-yellow-300" : "text-yellow-800",
              };
            default:
              return {
                bg: isDarkMode
                  ? "bg-blue-900/30 border-blue-600"
                  : "bg-blue-50 border-blue-300",
                icon: "i",
                iconBg: "bg-blue-500",
                textColor: isDarkMode ? "text-blue-300" : "text-blue-800",
              };
          }
        };

        const renderAuditItem = (item, index) => {
          if (!item.Title || !item.Feedback || !item.Status) return null;

          const statusColor = getStatusColor(item.Status);

          return (
            <div
              key={index}
              className={`p-4 rounded-lg border-l-4 ${statusColor.bg}`}
            >
              <div className="flex items-start gap-3">
                {/* Status Icon */}
                <div
                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${statusColor.iconBg} text-white font-bold text-sm mt-1`}
                >
                  {statusColor.icon}
                </div>

                <div className="flex-1 min-w-0">
                  {/* Title */}
                  <h5
                    className={`font-semibold text-sm mb-2 ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                  >
                    #{index + 1} {item.Title}
                  </h5>

                  {/* Feedback */}
                  <p
                    className={`text-sm leading-relaxed mb-3 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                  >
                    {item.Feedback}
                  </p>

                  {/* Status Badge */}
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${statusColor.textColor} ${statusColor.bg}`}
                  >
                    {item.Status}
                  </span>
                </div>
              </div>
            </div>
          );
        };

        return (
          <div className="space-y-6">
            {/* Summary Section */}
            {typeof summaryItem === "object" && !Array.isArray(summaryItem) && (
              <div
                className={`p-4 rounded-lg border ${isDarkMode ? "border-blue-700 bg-blue-900/20" : "border-blue-300 bg-blue-50"}`}
              >
                <h4
                  className={`text-sm font-semibold mb-3 ${isDarkMode ? "text-blue-200" : "text-blue-900"}`}
                >
                  Summary
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {Object.entries(summaryItem).map(([key, value]) => (
                    <div
                      key={key}
                      className={`p-2 rounded ${isDarkMode ? "bg-blue-800/30" : "bg-white"}`}
                    >
                      <p
                        className={`text-xs font-medium mb-1 ${isDarkMode ? "text-blue-300" : "text-blue-700"}`}
                      >
                        {formatLabel(key)}
                      </p>
                      <p
                        className={`text-lg font-bold ${isDarkMode ? "text-blue-100" : "text-blue-900"}`}
                      >
                        {formatValue(value)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Negative Items Dropdown */}
            {negativeItems.length > 0 && (
              <div
                className={`rounded-lg border ${isDarkMode ? "border-red-700 bg-red-900/10" : "border-red-300 bg-red-50/50"}`}
              >
                <button
                  onClick={() => setShowNegative(!showNegative)}
                  className={`w-full p-4 flex items-center justify-between transition-colors ${
                    isDarkMode ? "hover:bg-red-900/20" : "hover:bg-red-100/50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-red-500 text-white font-bold">
                      ✕
                    </div>
                    <div className="text-left">
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-red-200" : "text-red-900"}`}
                      >
                        Issues to Fix
                      </h4>
                      <p
                        className={`text-xs ${isDarkMode ? "text-red-300" : "text-red-700"}`}
                      >
                        {negativeItems.length}{" "}
                        {negativeItems.length === 1 ? "item" : "items"}
                      </p>
                    </div>
                  </div>
                  {showNegative ? (
                    <FaChevronUp
                      className={isDarkMode ? "text-red-300" : "text-red-700"}
                    />
                  ) : (
                    <FaChevronDown
                      className={isDarkMode ? "text-red-300" : "text-red-700"}
                    />
                  )}
                </button>

                {showNegative && (
                  <div className="p-4 pt-0 space-y-3">
                    {negativeItems.map((item, index) =>
                      renderAuditItem(item, index),
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Positive Items Dropdown */}
            {positiveItems.length > 0 && (
              <div
                className={`rounded-lg border ${isDarkMode ? "border-green-700 bg-green-900/10" : "border-green-300 bg-green-50/50"}`}
              >
                <button
                  onClick={() => setShowPositive(!showPositive)}
                  className={`w-full p-4 flex items-center justify-between transition-colors ${
                    isDarkMode
                      ? "hover:bg-green-900/20"
                      : "hover:bg-green-100/50"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-green-500 text-white font-bold">
                      ✓
                    </div>
                    <div className="text-left">
                      <h4
                        className={`text-sm font-semibold ${isDarkMode ? "text-green-200" : "text-green-900"}`}
                      >
                        Passed Audits
                      </h4>
                      <p
                        className={`text-xs ${isDarkMode ? "text-green-300" : "text-green-700"}`}
                      >
                        {positiveItems.length}{" "}
                        {positiveItems.length === 1 ? "item" : "items"}
                      </p>
                    </div>
                  </div>
                  {showPositive ? (
                    <FaChevronUp
                      className={
                        isDarkMode ? "text-green-300" : "text-green-700"
                      }
                    />
                  ) : (
                    <FaChevronDown
                      className={
                        isDarkMode ? "text-green-300" : "text-green-700"
                      }
                    />
                  )}
                </button>

                {showPositive && (
                  <div className="p-4 pt-0 space-y-3">
                    {positiveItems.map((item, index) =>
                      renderAuditItem(item, index),
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      }

      return (
        <div className="space-y-4">
          {actualData.map((item, index) => {
            const isItemArray = Array.isArray(item);
            const isItemObject =
              typeof item === "object" && !isItemArray && item !== null;

            return (
              <div
                key={index}
                className={`p-4 rounded-lg border ${isDarkMode ? "border-gray-700 bg-gray-700/30" : "border-gray-200 bg-white"}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h4
                    className={`text-sm font-semibold ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                  >
                    {isItemArray
                      ? `Details (${item.length} items)`
                      : `Item #${index + 1}`}
                  </h4>
                </div>

                {isItemArray ? (
                  // Handle array of objects (like PWA audit details)
                  <div className="space-y-3">
                    {item.map((subItem, subIndex) => (
                      <div
                        key={subIndex}
                        className={`p-3 rounded border ${isDarkMode ? "border-gray-600 bg-gray-800/50" : "border-gray-300 bg-gray-50"}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span
                            className={`text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                          >
                            #{subIndex + 1}
                          </span>
                        </div>
                        {typeof subItem === "object" &&
                        !Array.isArray(subItem) ? (
                          <div className="space-y-2">
                            {Object.entries(subItem)
                              .filter(([key]) => !isFeedbackDetailsKey(key))
                              .map(([key, value]) => (
                                <div key={key} className="flex flex-col gap-1">
                                  <span
                                    className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                  >
                                    {formatLabel(key)}
                                  </span>
                                  <span
                                    className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-800"} break-words`}
                                  >
                                    {formatValue(value)}
                                  </span>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p
                            className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                          >
                            {formatValue(subItem)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : isItemObject ? (
                  // Handle single object
                  <div className="space-y-2">
                    {Object.entries(item)
                      .filter(([key]) => !isFeedbackDetailsKey(key))
                      .map(([key, value]) => (
                        <div
                          key={key}
                          className={`p-3 rounded border-l-2 ${isDarkMode ? "border-l-blue-500 bg-gray-800/50" : "border-l-blue-400 bg-blue-50"}`}
                        >
                          <p
                            className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            {formatLabel(key)}
                          </p>
                          <p
                            className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-800"} break-words`}
                          >
                            {formatValue(value)}
                          </p>
                        </div>
                      ))}
                  </div>
                ) : (
                  // Handle primitive values
                  <pre
                    className={`overflow-x-auto text-sm font-mono ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                  >
                    {formatValue(item)}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // Handle non-object primitives
    if (!isObject) {
      return (
        <pre
          className={`overflow-x-auto text-sm font-mono ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
        >
          {formatValue(actualData)}
        </pre>
      );
    }

    const entries = Object.entries(actualData).filter(
      ([key]) => !isFeedbackDetailsKey(key),
    );

    if (entries.length === 0) {
      return (
        <p
          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
        >
          No data available for this endpoint.
        </p>
      );
    }

    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {entries.map(([key, value]) => {
          const isArrayValue = Array.isArray(value);
          const isObjectValue =
            typeof value === "object" && !isArrayValue && value !== null;

          return (
            <div
              key={key}
              className={`p-3 rounded-lg border ${isDarkMode ? "border-gray-700 bg-gray-700/30" : "border-gray-200 bg-white"} ${
                isArrayValue || isObjectValue ? "sm:col-span-2" : ""
              }`}
            >
              <p
                className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                {formatLabel(key)}
              </p>

              {isArrayValue ? (
                value.length === 0 ? (
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    Empty
                  </p>
                ) : typeof value[0] === "object" ? (
                  <div className="space-y-3">
                    {value.map((item, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded border ${isDarkMode ? "border-gray-600 bg-gray-800/30" : "border-gray-300 bg-gray-50"}`}
                      >
                        <div className="space-y-2">
                          {Object.entries(item)
                            .filter(
                              ([itemKey]) => !isFeedbackDetailsKey(itemKey),
                            )
                            .map(([itemKey, itemValue]) => (
                              <div
                                key={itemKey}
                                className="flex flex-col sm:flex-row sm:items-start gap-1"
                              >
                                <span
                                  className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"} sm:min-w-[120px]`}
                                >
                                  {formatLabel(itemKey)}:
                                </span>
                                <span
                                  className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-800"} break-words`}
                                >
                                  {formatValue(itemValue)}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                  >
                    {value.join(", ")}
                  </p>
                )
              ) : isObjectValue ? (
                <div className="space-y-2">
                  {Object.entries(value)
                    .filter(([subKey]) => !isFeedbackDetailsKey(subKey))
                    .map(([subKey, subValue]) => (
                      <div
                        key={subKey}
                        className="flex flex-col sm:flex-row sm:items-start gap-1"
                      >
                        <span
                          className={`text-xs font-medium ${isDarkMode ? "text-gray-400" : "text-gray-600"} sm:min-w-[120px]`}
                        >
                          {formatLabel(subKey)}:
                        </span>
                        <span
                          className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-800"} break-words`}
                        >
                          {formatValue(subValue)}
                        </span>
                      </div>
                    ))}
                </div>
              ) : (
                <p
                  className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-800"} break-words`}
                >
                  {formatValue(value)}
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const isHeadingEndpoint = /^(headings|heading|h[1-6])$/.test(
    (endpointId || "").toLowerCase(),
  );
  const isHelpfulContentEndpoint = /^(helpful_content|helpful content)$/i.test(
    (endpointId || "").replace(/\s+/g, "_"),
  );

  // AI Suggestions are only available for: meta, title, heading, helpful content
  const hasAiSuggestions =
    /^(meta|meta_description|title|title_tag|seo_content|headings|heading|h[1-6]|helpful_content|helpful content)$/i.test(
      (endpointId || "").replace(/\s+/g, "_"),
    );

  const isHelpfulContentSuggestion = (suggestion) => {
    if (!suggestion || typeof suggestion !== "object") return false;
    const type = (suggestion.type || "").toLowerCase();
    return type === "content";
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen p-4 sm:p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
      >
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p
              className={`text-sm sm:text-base ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
            >
              Loading endpoint details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!endpoint) {
    return (
      <div
        className={`min-h-screen p-4 sm:p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
      >
        <button
          onClick={() =>
            navigate(`/dashboard/seo-page-results/${projectId}`, {
              state: { runId: state?.runId, page: state?.page },
            })
          }
          className={`p-2 rounded-lg transition-colors mb-4 sm:mb-6 ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-white"}`}
        >
          <FaArrowLeft
            className={`text-lg sm:text-xl ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          />
        </button>
        <div className="text-center">
          <p
            className={`text-sm sm:text-base ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            Endpoint not found
          </p>
          {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen p-4 sm:p-6 ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
          <button
            onClick={() =>
              navigate(`/dashboard/seo-page-results/${projectId}`, {
                state: { runId: state?.runId, page: state?.page },
              })
            }
            className={`p-2 rounded-lg transition-colors flex-shrink-0 ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-white"}`}
          >
            <FaArrowLeft
              className={`text-lg sm:text-xl ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
            />
          </button>
          <div className="min-w-0">
            <h1
              className={`text-xl sm:text-2xl md:text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {endpoint.icon} {endpoint.name}
            </h1>
            <p
              className={`text-xs sm:text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"} break-all truncate`}
            >
              {endpoint?.page_url || state?.page?.page_url || "No URL"}
            </p>
            <p
              className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-500"} mt-1`}
            >
              Run ID: {state?.runId || "N/A"}
            </p>
          </div>
        </div>

        <div
          className={`p-4 sm:p-6 rounded-lg shadow-lg mb-4 sm:mb-6 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0 mb-4">
            <div className="text-center sm:text-left">
              <h2
                className={`text-xl sm:text-2xl font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Endpoint Score
              </h2>
              {error && <p className="text-sm text-red-500">{error}</p>}
            </div>
            <div
              className={`text-center p-5 sm:p-6 rounded-full ${getScoreBgColor(endpoint.score)}`}
            >
              <div
                className={`text-4xl sm:text-5xl font-bold ${getScoreColor(endpoint.score)}`}
              >
                {endpoint.score}%
              </div>
            </div>
          </div>
        </div>

        <div
          className={`p-4 sm:p-6 rounded-lg shadow-lg mb-4 sm:mb-6 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <h3
            className={`text-lg sm:text-xl font-semibold mb-3 sm:mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Raw Data
          </h3>
          <div
            className={`p-3 sm:p-4 rounded-lg ${isDarkMode ? "bg-gray-700" : "bg-gray-50"}`}
          >
            {renderRawDataContent()}
          </div>
        </div>

        {/* AI Suggestions - Full Width */}
        {!isHeadingEndpoint && (
          <div
            className={`p-4 sm:p-6 rounded-lg shadow-lg ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
          >
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3
                className={`text-lg sm:text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                AI Suggestions
              </h3>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {!hasAiSuggestions ? (
                <div
                  className={`text-center py-8 px-4 rounded-lg ${isDarkMode ? "bg-gray-700/50" : "bg-gray-100"}`}
                >
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    No AI suggestions available for this endpoint
                  </p>
                </div>
              ) : loadingSuggestions ? (
                <div
                  className={`text-center py-8 px-4 rounded-lg ${isDarkMode ? "bg-gray-700/50" : "bg-gray-100"}`}
                >
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    Loading AI suggestions...
                  </p>
                </div>
              ) : !aiSuggestions && !generatingSuggestions ? (
                <div className="text-center py-6">
                  <p
                    className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    Click below to generate AI suggestions for this endpoint
                  </p>
                  <button
                    onClick={() => generateAiSuggestions()}
                    disabled={!currentPageId}
                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                      !currentPageId
                        ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                        : isDarkMode
                          ? "bg-blue-600 hover:bg-blue-700 text-white"
                          : "bg-blue-600 hover:bg-blue-700 text-white"
                    }`}
                  >
                    Get Suggestions
                  </button>
                </div>
              ) : aiSuggestions &&
                !generatingSuggestions &&
                (() => {
                  // Validate that aiSuggestions has actual data before rendering
                  const hasValidData = isHelpfulContentEndpoint
                    ? isHelpfulContentSuggestion(aiSuggestions) &&
                      ((aiSuggestions.suggestions &&
                        Array.isArray(aiSuggestions.suggestions) &&
                        aiSuggestions.suggestions.length > 0) ||
                        (aiSuggestions.items &&
                          Array.isArray(aiSuggestions.items) &&
                          aiSuggestions.items.length > 0 &&
                          aiSuggestions.items.some(
                            (item) =>
                              item.current ||
                              item.current_text ||
                              item.variations?.length > 0 ||
                              item.issues?.length > 0,
                          )) ||
                        (aiSuggestions.variations &&
                          Array.isArray(aiSuggestions.variations) &&
                          aiSuggestions.variations.length > 0) ||
                        aiSuggestions.issue_summary ||
                        aiSuggestions.priority)
                    : (aiSuggestions.suggestions &&
                        Array.isArray(aiSuggestions.suggestions) &&
                        aiSuggestions.suggestions.length > 0) ||
                      (aiSuggestions.items &&
                        Array.isArray(aiSuggestions.items) &&
                        aiSuggestions.items.length > 0 &&
                        aiSuggestions.items.some(
                          (item) =>
                            item.current ||
                            item.current_text ||
                            item.variations?.length > 0 ||
                            item.issues?.length > 0,
                        )) ||
                      (aiSuggestions.variations &&
                        Array.isArray(aiSuggestions.variations) &&
                        aiSuggestions.variations.length > 0) ||
                      aiSuggestions.issue_summary ||
                      aiSuggestions.priority;

                  return hasValidData;
                })() ? (
                <>
                  {/* Priority Badge */}
                  {!isHelpfulContentEndpoint && aiSuggestions.priority && (
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded ${
                          aiSuggestions.priority === "critical"
                            ? "bg-red-100 text-red-800"
                            : aiSuggestions.priority === "high"
                              ? "bg-orange-100 text-orange-800"
                              : aiSuggestions.priority === "medium"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-green-100 text-green-800"
                        }`}
                      >
                        {aiSuggestions.priority.toUpperCase()} PRIORITY
                      </span>
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded ${isDarkMode ? "bg-blue-700 text-blue-100" : "bg-blue-200 text-blue-800"}`}
                      >
                        {aiSuggestions.auto_mode === "full"
                          ? "FULL AUTO"
                          : aiSuggestions.auto_mode === "semi"
                            ? "SEMI AUTO"
                            : "MANUAL"}
                      </span>
                    </div>
                  )}

                  {/* Issue Summary */}
                  {!isHelpfulContentEndpoint && aiSuggestions.issue_summary && (
                    <div
                      className={`p-3 rounded-lg border ${isDarkMode ? "border-blue-700 bg-blue-900/20" : "border-blue-200 bg-blue-50"}`}
                    >
                      <p
                        className={`text-sm ${isDarkMode ? "text-blue-200" : "text-blue-900"}`}
                      >
                        {aiSuggestions.issue_summary}
                      </p>
                    </div>
                  )}

                  {/* Helpful Content Suggestions */}
                  {aiSuggestions.suggestions &&
                    Array.isArray(aiSuggestions.suggestions) &&
                    (() => {
                      return (
                        <div className="space-y-4">
                          {aiSuggestions.suggestions.map((suggestion, idx) => (
                            <div
                              key={idx}
                              className={`p-5 rounded-lg border-2 ${isDarkMode ? "border-gray-700 bg-gray-800/50" : "border-gray-300 bg-white shadow-sm"}`}
                            >
                              <div className="flex items-start gap-3 mb-4">
                                <span
                                  className={`px-3 py-1.5 rounded-lg text-xs font-bold flex-shrink-0 ${
                                    isDarkMode
                                      ? "bg-blue-700 text-blue-100"
                                      : "bg-blue-100 text-blue-800"
                                  }`}
                                >
                                  {suggestion.type?.toUpperCase() ||
                                    `#${idx + 1}`}
                                </span>
                                {suggestion.priority && (
                                  <span
                                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg flex-shrink-0 ${
                                      suggestion.priority === "critical"
                                        ? "bg-red-100 text-red-800"
                                        : suggestion.priority === "high"
                                          ? "bg-orange-100 text-orange-800"
                                          : suggestion.priority === "medium"
                                            ? "bg-yellow-100 text-yellow-800"
                                            : "bg-green-100 text-green-800"
                                    }`}
                                  >
                                    {suggestion.priority.toUpperCase()}
                                  </span>
                                )}
                              </div>

                              {suggestion.issue_summary && (
                                <p
                                  className={`text-sm mb-4 font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                                >
                                  {suggestion.issue_summary}
                                </p>
                              )}

                              {/* Issues First - Red Styling */}
                              {suggestion.issues &&
                                suggestion.issues.length > 0 && (
                                  <div
                                    className={`mb-4 p-4 rounded-lg border-2 border-l-4 ${isDarkMode ? "border-red-700 bg-red-900/20" : "border-red-300 bg-red-50"}`}
                                  >
                                    <p
                                      className={`text-sm font-bold mb-3 flex items-center gap-2 ${isDarkMode ? "text-red-300" : "text-red-800"}`}
                                    >
                                      <span className="text-lg">⚠️</span>
                                      Issues Found:
                                    </p>
                                    <ul className="space-y-2">
                                      {suggestion.issues.map((issue, iIdx) => (
                                        <li
                                          key={iIdx}
                                          className={`text-sm p-2 rounded ${isDarkMode ? "bg-red-900/30 text-red-200" : "bg-red-100 text-red-800"}`}
                                        >
                                          • {issue.detail || issue}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                              {/* Suggested Variations - Green Styling */}
                              {suggestion.variations &&
                                suggestion.variations.length > 0 && (
                                  <div
                                    className={`p-4 rounded-lg border-2 ${isDarkMode ? "border-green-700 bg-green-900/20" : "border-green-300 bg-green-50"}`}
                                  >
                                    <p
                                      className={`text-sm font-bold mb-3 flex items-center gap-2 ${isDarkMode ? "text-green-300" : "text-green-800"}`}
                                    >
                                      <span className="text-lg">✨</span>
                                      Suggested Variations:
                                    </p>
                                    <div className="space-y-3">
                                      {suggestion.variations.map(
                                        (variation, vIdx) => {
                                          const varText =
                                            variation.text || variation;
                                          const varCharCount = varText
                                            ? varText.length
                                            : 0;
                                          const isSelected =
                                            selectedSuggestion === varText;
                                          return (
                                            <div
                                              key={vIdx}
                                              onClick={() =>
                                                setSelectedSuggestion(
                                                  isSelected ? null : varText,
                                                )
                                              }
                                              className={`p-3 rounded-lg border-2 cursor-pointer transition-all shadow-sm ${
                                                isSelected
                                                  ? isDarkMode
                                                    ? "border-blue-500 bg-blue-900/40 shadow-blue-500/50"
                                                    : "border-blue-500 bg-blue-100 shadow-blue-200"
                                                  : isDarkMode
                                                    ? "border-green-600 bg-green-900/30 hover:border-green-500 hover:shadow-md"
                                                    : "border-green-400 bg-white hover:border-green-500 hover:shadow-md"
                                              }`}
                                            >
                                              <div className="flex items-start gap-3">
                                                <input
                                                  type="checkbox"
                                                  checked={isSelected}
                                                  onChange={() =>
                                                    setSelectedSuggestion(
                                                      isSelected
                                                        ? null
                                                        : varText,
                                                    )
                                                  }
                                                  className="mt-0.5 h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                />
                                                <div className="flex-1">
                                                  <div className="flex items-center justify-between mb-1">
                                                    <span
                                                      className={`text-xs font-bold ${isDarkMode ? "text-green-400" : "text-green-700"}`}
                                                    >
                                                      Option {vIdx + 1}
                                                    </span>
                                                    <span
                                                      className={`text-xs font-semibold px-2 py-0.5 rounded ${isDarkMode ? "bg-green-900/50 text-green-300" : "bg-green-200 text-green-700"}`}
                                                    >
                                                      {varCharCount} characters
                                                    </span>
                                                  </div>
                                                  <p
                                                    className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                                                  >
                                                    {varText}
                                                  </p>
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        },
                                      )}
                                    </div>
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                  {/* Issues First - Full Auto Mode (title, meta, h1) */}
                  {aiSuggestions.auto_mode === "full" &&
                    aiSuggestions.issues &&
                    aiSuggestions.issues.length > 0 && (
                      <div
                        className={`rounded-lg border-2 ${isDarkMode ? "border-red-700 bg-red-900/20" : "border-red-300 bg-red-50"}`}
                      >
                        <button
                          onClick={() => setShowIssuesFound(!showIssuesFound)}
                          className={`w-full p-4 flex items-center justify-between transition-colors ${
                            isDarkMode
                              ? "hover:bg-red-900/30"
                              : "hover:bg-red-100"
                          }`}
                        >
                          <h4
                            className={`text-sm font-bold flex items-center gap-2 ${isDarkMode ? "text-red-300" : "text-red-800"}`}
                          >
                            <span className="text-lg">⚠️</span>
                            Issues Found ({aiSuggestions.issues.length})
                          </h4>
                          {showIssuesFound ? (
                            <FaChevronUp
                              className={
                                isDarkMode ? "text-red-400" : "text-red-600"
                              }
                            />
                          ) : (
                            <FaChevronDown
                              className={
                                isDarkMode ? "text-red-400" : "text-red-600"
                              }
                            />
                          )}
                        </button>

                        {showIssuesFound && (
                          <div
                            className={`p-4 pt-3 border-t-2 ${isDarkMode ? "border-red-700" : "border-red-300"}`}
                          >
                            <div className="space-y-2">
                              {aiSuggestions.issues.map((issue, idx) => (
                                <div
                                  key={idx}
                                  className={`p-3 rounded-lg border-l-4 ${isDarkMode ? "border-red-500 bg-red-900/30" : "border-red-500 bg-red-100"}`}
                                >
                                  <p
                                    className={`text-sm font-medium ${isDarkMode ? "text-red-200" : "text-red-900"}`}
                                  >
                                    {issue.detail || issue}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                  {/* Suggested Variations - Full Auto Mode */}
                  {aiSuggestions.auto_mode === "full" &&
                    aiSuggestions.variations &&
                    aiSuggestions.variations.length > 0 && (
                      <div
                        className={`rounded-lg border-2 ${isDarkMode ? "border-green-700 bg-green-900/20" : "border-green-300 bg-green-50"}`}
                      >
                        <button
                          onClick={() =>
                            setShowSuggestedVariations(!showSuggestedVariations)
                          }
                          className={`w-full p-4 flex items-center justify-between transition-colors ${
                            isDarkMode
                              ? "hover:bg-green-900/30"
                              : "hover:bg-green-100"
                          }`}
                        >
                          <h4
                            className={`text-sm font-bold flex items-center gap-2 ${isDarkMode ? "text-green-300" : "text-green-800"}`}
                          >
                            <span className="text-lg">✨</span>
                            Suggested Variations (
                            {aiSuggestions.variations.length})
                          </h4>
                          {showSuggestedVariations ? (
                            <FaChevronUp
                              className={
                                isDarkMode ? "text-green-400" : "text-green-600"
                              }
                            />
                          ) : (
                            <FaChevronDown
                              className={
                                isDarkMode ? "text-green-400" : "text-green-600"
                              }
                            />
                          )}
                        </button>

                        {showSuggestedVariations && (
                          <div
                            className={`p-4 pt-3 border-t-2 ${isDarkMode ? "border-green-700" : "border-green-300"}`}
                          >
                            <div className="space-y-3">
                              {aiSuggestions.variations.map(
                                (variation, idx) => {
                                  const suggestionText =
                                    variation.text || variation;
                                  const charCount = suggestionText
                                    ? suggestionText.length
                                    : 0;
                                  const isSelected =
                                    selectedSuggestion === suggestionText;
                                  return (
                                    <div
                                      key={idx}
                                      onClick={() =>
                                        setSelectedSuggestion(
                                          isSelected ? null : suggestionText,
                                        )
                                      }
                                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all shadow-sm ${
                                        isSelected
                                          ? isDarkMode
                                            ? "border-blue-500 bg-blue-900/40 shadow-blue-500/50"
                                            : "border-blue-500 bg-blue-100 shadow-blue-200"
                                          : isDarkMode
                                            ? "border-green-600 bg-green-900/20 hover:border-green-500 hover:shadow-md"
                                            : "border-green-400 bg-white hover:border-green-500 hover:shadow-md"
                                      }`}
                                    >
                                      <div className="flex items-start gap-3">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() =>
                                            setSelectedSuggestion(
                                              isSelected
                                                ? null
                                                : suggestionText,
                                            )
                                          }
                                          className="mt-1 h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                                          onClick={(e) => e.stopPropagation()}
                                        />
                                        <div className="flex-1">
                                          <div className="flex items-center justify-between mb-1">
                                            <span
                                              className={`text-xs font-bold ${isDarkMode ? "text-green-400" : "text-green-700"}`}
                                            >
                                              Option {idx + 1}
                                            </span>
                                            <span
                                              className={`text-xs font-semibold px-2 py-0.5 rounded ${isDarkMode ? "bg-green-900/50 text-green-300" : "bg-green-200 text-green-700"}`}
                                            >
                                              {charCount} characters
                                            </span>
                                          </div>
                                          <p
                                            className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                                          >
                                            {suggestionText}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                },
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                  {/* Semi Auto Mode - Items with Variations (h2, h3, h4, content) */}
                  {aiSuggestions.auto_mode === "semi" &&
                    aiSuggestions.items &&
                    aiSuggestions.items.length > 0 &&
                    (() => {
                      // Only show if items have actual content (current text, variations, or issues)
                      const hasValidItems = aiSuggestions.items.some(
                        (item) =>
                          item.current ||
                          item.current_text ||
                          item.variations?.length > 0 ||
                          item.issues?.length > 0,
                      );
                      return hasValidItems;
                    })() && (
                      <div
                        className={`p-4 rounded-lg border-2 ${isDarkMode ? "border-gray-700 bg-gray-800/50" : "border-gray-300 bg-gray-100"}`}
                      >
                        <h4
                          className={`text-lg font-bold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {isHelpfulContentEndpoint
                            ? `Suggested Items (${aiSuggestions.items.filter((item) => item.current || item.current_text || item.variations?.length > 0 || item.issues?.length > 0).length})`
                            : `Suggestions (${aiSuggestions.items.filter((item) => item.current || item.current_text || item.variations?.length > 0 || item.issues?.length > 0).length} items)`}
                        </h4>
                        <div className="space-y-4">
                          {isHelpfulContentEndpoint
                            ? aiSuggestions.items
                                .filter(
                                  (item) =>
                                    // Only render items that have actual content
                                    item.current ||
                                    item.current_text ||
                                    item.variations?.length > 0 ||
                                    item.issues?.length > 0,
                                )
                                .map((item, idx) => {
                                  const itemKey = `content-${idx}`;
                                  const issuesKey = `${itemKey}-issues`;
                                  const variationsKey = `${itemKey}-variations`;
                                  const isItemExpanded =
                                    expandedContentItems[itemKey] ?? false;
                                  const isIssuesExpanded =
                                    expandedContentIssues[issuesKey] ?? false;
                                  const isVariationsExpanded =
                                    expandedContentVariations[variationsKey] ??
                                    false;
                                  const currentText =
                                    item.current || item.current_text || "";
                                  const previewText =
                                    currentText.length > 120
                                      ? `${currentText.slice(0, 120)}...`
                                      : currentText || "No content available";

                                  return (
                                    <div
                                      key={itemKey}
                                      className={`rounded-lg border-2 overflow-hidden ${isDarkMode ? "border-gray-600 bg-gray-800/70" : "border-gray-300 bg-white shadow-sm"}`}
                                    >
                                      <button
                                        onClick={() =>
                                          setExpandedContentItems((prev) => ({
                                            ...prev,
                                            [itemKey]: !isItemExpanded,
                                          }))
                                        }
                                        className={`w-full p-4 flex items-center justify-between transition-colors ${isDarkMode ? "hover:bg-gray-800/80" : "hover:bg-gray-50"}`}
                                      >
                                        <div className="text-left">
                                          <p
                                            className={`text-sm font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
                                          >
                                            Paragraph {idx + 1}
                                          </p>
                                          <p
                                            className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                          >
                                            {previewText}
                                          </p>
                                        </div>
                                        {isItemExpanded ? (
                                          <FaChevronUp
                                            className={
                                              isDarkMode
                                                ? "text-gray-400"
                                                : "text-gray-600"
                                            }
                                          />
                                        ) : (
                                          <FaChevronDown
                                            className={
                                              isDarkMode
                                                ? "text-gray-400"
                                                : "text-gray-600"
                                            }
                                          />
                                        )}
                                      </button>

                                      {isItemExpanded && (
                                        <div
                                          className={`p-4 pt-3 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
                                        >
                                          {/* Issues Dropdown */}
                                          <div
                                            className={`rounded-lg border ${isDarkMode ? "border-red-700 bg-red-900/10" : "border-red-300 bg-red-50/50"} mb-3`}
                                          >
                                            <button
                                              onClick={() =>
                                                setExpandedContentIssues(
                                                  (prev) => ({
                                                    ...prev,
                                                    [issuesKey]:
                                                      !isIssuesExpanded,
                                                  }),
                                                )
                                              }
                                              className={`w-full p-3 flex items-center justify-between transition-colors ${isDarkMode ? "hover:bg-red-900/20" : "hover:bg-red-100/50"}`}
                                            >
                                              <div className="flex items-center gap-2">
                                                <span className="text-sm">
                                                  ⚠️
                                                </span>
                                                <span
                                                  className={`text-sm font-semibold ${isDarkMode ? "text-red-200" : "text-red-800"}`}
                                                >
                                                  Issues (
                                                  {item.issues?.length || 0})
                                                </span>
                                              </div>
                                              {isIssuesExpanded ? (
                                                <FaChevronUp
                                                  className={
                                                    isDarkMode
                                                      ? "text-red-300"
                                                      : "text-red-700"
                                                  }
                                                />
                                              ) : (
                                                <FaChevronDown
                                                  className={
                                                    isDarkMode
                                                      ? "text-red-300"
                                                      : "text-red-700"
                                                  }
                                                />
                                              )}
                                            </button>
                                            {isIssuesExpanded && (
                                              <div className="p-3 pt-0 space-y-2">
                                                {item.issues &&
                                                item.issues.length > 0 ? (
                                                  item.issues.map(
                                                    (issue, iIdx) => (
                                                      <p
                                                        key={iIdx}
                                                        className={`text-xs p-2 rounded ${isDarkMode ? "bg-red-900/30 text-red-200" : "bg-red-100 text-red-800"}`}
                                                      >
                                                        •{" "}
                                                        {issue.detail || issue}
                                                      </p>
                                                    ),
                                                  )
                                                ) : (
                                                  <p
                                                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                                  >
                                                    No issues found for this
                                                    paragraph.
                                                  </p>
                                                )}
                                              </div>
                                            )}
                                          </div>

                                          {/* Variations Dropdown */}
                                          <div
                                            className={`rounded-lg border ${isDarkMode ? "border-green-700 bg-green-900/10" : "border-green-300 bg-green-50/50"}`}
                                          >
                                            <button
                                              onClick={() =>
                                                setExpandedContentVariations(
                                                  (prev) => ({
                                                    ...prev,
                                                    [variationsKey]:
                                                      !isVariationsExpanded,
                                                  }),
                                                )
                                              }
                                              className={`w-full p-3 flex items-center justify-between transition-colors ${isDarkMode ? "hover:bg-green-900/20" : "hover:bg-green-100/50"}`}
                                            >
                                              <div className="flex items-center gap-2">
                                                <span className="text-sm">
                                                  ✨
                                                </span>
                                                <span
                                                  className={`text-sm font-semibold ${isDarkMode ? "text-green-200" : "text-green-800"}`}
                                                >
                                                  Suggested Variations (
                                                  {item.variations?.length || 0}
                                                  )
                                                </span>
                                              </div>
                                              {isVariationsExpanded ? (
                                                <FaChevronUp
                                                  className={
                                                    isDarkMode
                                                      ? "text-green-300"
                                                      : "text-green-700"
                                                  }
                                                />
                                              ) : (
                                                <FaChevronDown
                                                  className={
                                                    isDarkMode
                                                      ? "text-green-300"
                                                      : "text-green-700"
                                                  }
                                                />
                                              )}
                                            </button>
                                            {isVariationsExpanded && (
                                              <div className="p-3 pt-0 space-y-2">
                                                {item.variations &&
                                                item.variations.length > 0 ? (
                                                  item.variations.map(
                                                    (variation, vIdx) => {
                                                      const suggestionText =
                                                        variation.text ||
                                                        variation.html ||
                                                        variation;
                                                      const varCharCount =
                                                        suggestionText
                                                          ? suggestionText.length
                                                          : 0;
                                                      const isSelected =
                                                        selectedSuggestion ===
                                                        suggestionText;
                                                      return (
                                                        <div
                                                          key={`${variationsKey}-${vIdx}`}
                                                          onClick={() =>
                                                            setSelectedSuggestion(
                                                              isSelected
                                                                ? null
                                                                : suggestionText,
                                                            )
                                                          }
                                                          className={`p-3 rounded-lg border-2 cursor-pointer transition-all shadow-sm ${
                                                            isSelected
                                                              ? isDarkMode
                                                                ? "border-blue-500 bg-blue-900/40 shadow-blue-500/50"
                                                                : "border-blue-500 bg-blue-100 shadow-blue-200"
                                                              : isDarkMode
                                                                ? "border-green-600 bg-green-900/30 hover:border-green-500 hover:shadow-md"
                                                                : "border-green-400 bg-white hover:border-green-500 hover:shadow-md"
                                                          }`}
                                                        >
                                                          <div className="flex items-start gap-3">
                                                            <input
                                                              type="checkbox"
                                                              checked={
                                                                isSelected
                                                              }
                                                              onChange={() =>
                                                                setSelectedSuggestion(
                                                                  isSelected
                                                                    ? null
                                                                    : suggestionText,
                                                                )
                                                              }
                                                              className="mt-0.5 h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                                                              onClick={(e) =>
                                                                e.stopPropagation()
                                                              }
                                                            />
                                                            <div className="flex-1">
                                                              <div className="flex items-center justify-between mb-1">
                                                                <span
                                                                  className={`text-xs font-bold ${isDarkMode ? "text-green-400" : "text-green-700"}`}
                                                                >
                                                                  Option{" "}
                                                                  {vIdx + 1}
                                                                </span>
                                                                <span
                                                                  className={`text-xs font-semibold px-2 py-0.5 rounded ${isDarkMode ? "bg-green-900/50 text-green-300" : "bg-green-200 text-green-700"}`}
                                                                >
                                                                  {varCharCount}{" "}
                                                                  characters
                                                                </span>
                                                              </div>
                                                              <p
                                                                className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                                                              >
                                                                {suggestionText}
                                                              </p>
                                                            </div>
                                                          </div>
                                                        </div>
                                                      );
                                                    },
                                                  )
                                                ) : (
                                                  <p
                                                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                                  >
                                                    No variations available for
                                                    this paragraph.
                                                  </p>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })
                            : aiSuggestions.items
                                .filter(
                                  (item) =>
                                    // Only render items that have actual content
                                    item.current ||
                                    item.current_text ||
                                    item.variations?.length > 0 ||
                                    item.issues?.length > 0,
                                )
                                .map((item, idx) => (
                                  <div
                                    key={idx}
                                    className={`p-4 rounded-lg border-2 ${isDarkMode ? "border-gray-600 bg-gray-800/70" : "border-gray-300 bg-white shadow-sm"}`}
                                  >
                                    {/* Current Text */}
                                    <div className="mb-3">
                                      <span
                                        className={`text-xs font-bold ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                                      >
                                        Current #{idx + 1}:
                                      </span>
                                      <p
                                        className={`text-sm mt-1 font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                                      >
                                        {item.current || item.current_text}
                                      </p>
                                      {item.has_keyword !== undefined && (
                                        <span
                                          className={`inline-block mt-2 text-xs font-bold px-3 py-1 rounded-lg ${
                                            item.has_keyword
                                              ? "bg-green-100 text-green-800"
                                              : "bg-yellow-100 text-yellow-800"
                                          }`}
                                        >
                                          {item.has_keyword
                                            ? "✓ Has keyword"
                                            : "✗ Missing keyword"}
                                        </span>
                                      )}
                                    </div>

                                    {/* Issues First - Red Styling */}
                                    {item.issues && item.issues.length > 0 && (
                                      <div
                                        className={`mb-3 p-3 rounded-lg border-2 border-l-4 ${isDarkMode ? "border-red-700 bg-red-900/20" : "border-red-300 bg-red-50"}`}
                                      >
                                        <p
                                          className={`text-xs font-bold mb-2 flex items-center gap-2 ${isDarkMode ? "text-red-300" : "text-red-800"}`}
                                        >
                                          <span>⚠️</span>
                                          Issues:
                                        </p>
                                        {item.issues.map((issue, iIdx) => (
                                          <p
                                            key={iIdx}
                                            className={`text-xs p-2 rounded mb-1 ${isDarkMode ? "bg-red-900/30 text-red-200" : "bg-red-100 text-red-800"}`}
                                          >
                                            • {issue.detail || issue}
                                          </p>
                                        ))}
                                      </div>
                                    )}

                                    {/* Suggested Variations - Green Styling */}
                                    {item.variations &&
                                      item.variations.length > 0 && (
                                        <div
                                          className={`p-3 rounded-lg border-2 ${isDarkMode ? "border-green-700 bg-green-900/20" : "border-green-300 bg-green-50"}`}
                                        >
                                          <span
                                            className={`text-sm font-bold mb-3 flex items-center gap-2 block ${isDarkMode ? "text-green-300" : "text-green-800"}`}
                                          >
                                            <span>✨</span>
                                            Suggested variations:
                                          </span>
                                          <div className="space-y-2">
                                            {item.variations.map(
                                              (variation, vIdx) => {
                                                const suggestionText =
                                                  variation.text ||
                                                  variation.html ||
                                                  variation;
                                                const varCharCount =
                                                  suggestionText
                                                    ? suggestionText.length
                                                    : 0;
                                                const isSelected =
                                                  selectedSuggestion ===
                                                  suggestionText;
                                                return (
                                                  <div
                                                    key={vIdx}
                                                    onClick={() =>
                                                      setSelectedSuggestion(
                                                        isSelected
                                                          ? null
                                                          : suggestionText,
                                                      )
                                                    }
                                                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all shadow-sm ${
                                                      isSelected
                                                        ? isDarkMode
                                                          ? "border-blue-500 bg-blue-900/40 shadow-blue-500/50"
                                                          : "border-blue-500 bg-blue-100 shadow-blue-200"
                                                        : isDarkMode
                                                          ? "border-green-600 bg-green-900/30 hover:border-green-500 hover:shadow-md"
                                                          : "border-green-400 bg-white hover:border-green-500 hover:shadow-md"
                                                    }`}
                                                  >
                                                    <div className="flex items-start gap-3">
                                                      <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() =>
                                                          setSelectedSuggestion(
                                                            isSelected
                                                              ? null
                                                              : suggestionText,
                                                          )
                                                        }
                                                        className="mt-0.5 h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                                                        onClick={(e) =>
                                                          e.stopPropagation()
                                                        }
                                                      />
                                                      <div className="flex-1">
                                                        <div className="flex items-center justify-between mb-1">
                                                          <span
                                                            className={`text-xs font-bold ${isDarkMode ? "text-green-400" : "text-green-700"}`}
                                                          >
                                                            Option {vIdx + 1}
                                                          </span>
                                                          <span
                                                            className={`text-xs font-semibold px-2 py-0.5 rounded ${isDarkMode ? "bg-green-900/50 text-green-300" : "bg-green-200 text-green-700"}`}
                                                          >
                                                            {varCharCount}{" "}
                                                            characters
                                                          </span>
                                                        </div>
                                                        <p
                                                          className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                                                        >
                                                          {suggestionText}
                                                        </p>
                                                      </div>
                                                    </div>
                                                  </div>
                                                );
                                              },
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                ))}
                        </div>
                      </div>
                    )}

                  {/* Current Value Display (for full auto modes) */}
                  {aiSuggestions.current_value && (
                    <div
                      className={`p-3 rounded-lg border ${isDarkMode ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50"}`}
                    >
                      <span
                        className={`text-xs font-semibold ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                      >
                        Current Value:
                      </span>
                      <p
                        className={`text-sm mt-1 ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}
                      >
                        {aiSuggestions.current_value}
                      </p>
                      {aiSuggestions.current_length !== undefined && (
                        <p
                          className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                        >
                          Length: {aiSuggestions.current_length} /{" "}
                          {aiSuggestions.optimal_length} characters
                        </p>
                      )}
                    </div>
                  )}

                  {/* Implementation Details */}
                  {aiSuggestions.implementation && (
                    <div
                      className={`p-3 rounded-lg border ${isDarkMode ? "border-green-700 bg-green-900/20" : "border-green-200 bg-green-50"}`}
                    >
                      <h4
                        className={`text-sm font-semibold mb-2 ${isDarkMode ? "text-green-100" : "text-green-900"}`}
                      >
                        Implementation Info
                      </h4>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {aiSuggestions.estimated_impact && (
                          <div>
                            <span
                              className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                            >
                              Impact:{" "}
                            </span>
                            <span
                              className={`font-medium ${isDarkMode ? "text-green-200" : "text-green-800"}`}
                            >
                              {aiSuggestions.estimated_impact}%
                            </span>
                          </div>
                        )}
                        {aiSuggestions.seo_points_gain && (
                          <div>
                            <span
                              className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                            >
                              SEO Points:{" "}
                            </span>
                            <span
                              className={`font-medium ${isDarkMode ? "text-green-200" : "text-green-800"}`}
                            >
                              +{aiSuggestions.seo_points_gain}
                            </span>
                          </div>
                        )}
                        {aiSuggestions.implementation.method && (
                          <div className="col-span-2">
                            <span
                              className={`${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                            >
                              Method:{" "}
                            </span>
                            <span
                              className={`font-medium ${isDarkMode ? "text-green-200" : "text-green-800"}`}
                            >
                              {aiSuggestions.implementation.method}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              ) : aiSuggestions && !generatingSuggestions ? (
                // AI suggestions exist but don't have valid data - show message
                <div
                  className={`text-center py-8 px-4 rounded-lg ${isDarkMode ? "bg-gray-700/50" : "bg-gray-100"}`}
                >
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {isHelpfulContentEndpoint
                      ? "Helpful content suggestions are not available yet."
                      : "Loading AI suggestions..."}
                  </p>
                </div>
              ) : generatingSuggestions ? (
                // Show loader while generating
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                    <p
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                    >
                      Generating AI suggestions...
                    </p>
                  </div>
                </div>
              ) : endpoint.data?.suggestions ? (
                Array.isArray(endpoint.data.suggestions) ? (
                  endpoint.data.suggestions.map((suggestion, idx) => (
                    <div
                      key={idx}
                      onClick={() => setSelectedSuggestion(suggestion)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedSuggestion === suggestion
                          ? isDarkMode
                            ? "border-blue-500 bg-blue-900/30"
                            : "border-blue-500 bg-blue-50"
                          : isDarkMode
                            ? "border-gray-700 bg-gray-700/30 hover:border-gray-600"
                            : "border-gray-200 bg-gray-50 hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedSuggestion === suggestion}
                          onChange={() => setSelectedSuggestion(suggestion)}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <p
                          className={`text-sm flex-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          {suggestion}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p
                    className={`text-xs sm:text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    {endpoint.data.suggestions}
                  </p>
                )
              ) : suggestionAttempted ? (
                <p
                  className={`text-xs sm:text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                >
                  No AI suggestions available for this endpoint
                </p>
              ) : null}

              {/* Implement Button - Always show, disabled when no selection */}
              {aiSuggestions && (
                <div
                  className={`mt-6 pt-4 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                >
                  <button
                    onClick={() => handleImplementation(selectedSuggestion)}
                    disabled={!selectedSuggestion || implementing}
                    className={`w-full py-3 px-4 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                      !selectedSuggestion || implementing
                        ? "bg-gray-400 cursor-not-allowed text-gray-600"
                        : isDarkMode
                          ? "bg-green-600 hover:bg-green-700 text-white"
                          : "bg-green-600 hover:bg-green-700 text-white"
                    }`}
                  >
                    {implementing ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Implementing...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle />
                        Implement Suggestion
                      </>
                    )}
                  </button>
                  {selectedSuggestion ? (
                    <p
                      className={`text-xs text-center mt-2 ${isDarkMode ? "text-gray-500" : "text-gray-600"}`}
                    >
                      Selected suggestion will be applied to your.pngage
                    </p>
                  ) : (
                    <p
                      className={`text-xs text-center mt-2 ${isDarkMode ? "text-gray-500" : "text-gray-600"}`}
                    >
                      Please select a suggestion to implement
                    </p>
                  )}
                </div>
              )}

              {aiSuggestions && lastImplementation && (
                <div
                  className={`mt-4 p-4 rounded-xl border ${isDarkMode ? "border-blue-700 bg-gradient-to-br from-blue-900/30 to-blue-900/10" : "border-blue-200 bg-gradient-to-br from-blue-50 to-white"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p
                        className={`text-xs font-semibold uppercase tracking-wide ${isDarkMode ? "text-blue-300" : "text-blue-700"}`}
                      >
                        Last change applied
                      </p>
                      <p
                        className={`text-sm font-medium mt-1 ${isDarkMode ? "text-gray-100" : "text-gray-900"} break-words`}
                      >
                        {lastImplementation.implementedText}
                      </p>
                    </div>
                    <span
                      className={`text-[10px] px-2 py-1 rounded-full ${isDarkMode ? "bg-blue-800/60 text-blue-200" : "bg-blue-100 text-blue-800"}`}
                    >
                      Applied
                    </span>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <span
                      className={`text-[11px] ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      You can undo this change if needed.
                    </span>
                    <button
                      onClick={() =>
                        handleRollback(
                          lastImplementation.backupId,
                          lastImplementation.implementedText,
                        )
                      }
                      disabled={
                        rollingBackId === lastImplementation.backupId ||
                        lastImplementation.undone
                      }
                      className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                        rollingBackId === lastImplementation.backupId ||
                        lastImplementation.undone
                          ? "bg-gray-400 cursor-not-allowed text-gray-600"
                          : isDarkMode
                            ? "bg-red-600/30 hover:bg-red-600/50 text-red-300 border border-red-600/50"
                            : "bg-red-100 hover:bg-red-200 text-red-700 border border-red-300"
                      }`}
                    >
                      {rollingBackId === lastImplementation.backupId
                        ? "Undoing..."
                        : lastImplementation.undone
                          ? "Already undone"
                          : "Undo last change"}
                    </button>
                  </div>
                </div>
              )}

              {/* Implementation History with Rollback Option */}
              {implementations && implementations.length > 0 && (
                <div
                  className={`mt-6 pt-6 ${isDarkMode ? "border-t border-gray-700" : "border-t border-gray-200"}`}
                >
                  <h4
                    className={`text-sm font-semibold mb-3 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                  >
                    Implementation History
                  </h4>
                  <div className="space-y-2">
                    {implementations.map((impl) => (
                      <div
                        key={impl.id}
                        className={`p-3 rounded-lg border ${
                          impl.implementation_status === "rolled_back"
                            ? isDarkMode
                              ? "bg-gray-700/50 border-gray-600"
                              : "bg-gray-100 border-gray-200"
                            : isDarkMode
                              ? "bg-green-900/20 border-green-700/30"
                              : "bg-green-50 border-green-200"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p
                              className={`text-xs font-semibold mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                            >
                              {new Date(impl.implemented_at).toLocaleString()}
                            </p>
                            <p
                              className={`text-sm mb-1 break-words ${
                                impl.implementation_status === "rolled_back"
                                  ? isDarkMode
                                    ? "text-gray-400"
                                    : "text-gray-600"
                                  : isDarkMode
                                    ? "text-green-300"
                                    : "text-green-800"
                              }`}
                            >
                              {impl.implemented_text || impl.suggestion_text}
                            </p>
                            {impl.implementation_status === "rolled_back" && (
                              <p
                                className={`text-xs ${isDarkMode ? "text-yellow-400" : "text-yellow-700"}`}
                              >
                                ↩️ Rolled back
                              </p>
                            )}
                          </div>
                          {impl.implementation_status !== "rolled_back" && (
                            <button
                              onClick={() =>
                                handleRollback(impl.id, impl.implemented_text)
                              }
                              disabled={rollingBackId === impl.id}
                              className={`flex-shrink-0 px-2 py-1 rounded text-xs font-medium transition-colors ${
                                rollingBackId === impl.id
                                  ? "bg-gray-400 cursor-not-allowed text-gray-600"
                                  : isDarkMode
                                    ? "bg-red-600/30 hover:bg-red-600/50 text-red-300 border border-red-600/50"
                                    : "bg-red-100 hover:bg-red-200 text-red-700 border border-red-300"
                              }`}
                              title="Revert this implementation"
                            >
                              {rollingBackId === impl.id
                                ? "Rolling back..."
                                : "Rollback"}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* WordPress Modal */}
            {showWordPressModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 p-4">
                <div
                  className={`rounded-lg shadow-xl max-w-md w-full p-6 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
                >
                  <h3
                    className={`text-xl font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    WordPress Website?
                  </h3>
                  <p
                    className={`text-sm mb-6 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                  >
                    Is this a WordPress website? Auto implementation is only
                    available for WordPress sites.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={handleWordPressYes}
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                    >
                      Yes, it's WordPress
                    </button>
                    <button
                      onClick={handleWordPressNo}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                        isDarkMode
                          ? "bg-gray-700 hover:bg-gray-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300 text-gray-900"
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
         
        {/* --- Image Optimizer Section --- */}
        {(endpoint.name === 'Image Analysis' || endpointId === 'image_analysis') && (
          <ImageOptimizerSection 
            projectId={projectId} 
            pageUrl={endpoint?.page_url || state?.page?.page_url} 
          />
        )}
      </div>
    </div>
  );
};

export default PageEndpointDetailsPage;
